// Import necessary React hooks and icons
import {
  AlertCircle,
  Archive,
  Award,
  Building,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Eye,
  Flag,
  GraduationCap,
  Heart,
  Home,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Save,
  Shield,
  Trash2,
  Upload,
  User,
  UserCheck,
  Users,
  X,
  FileText,
  Download,
} from "lucide-react";
import { useContext, useEffect, useState, useRef } from "react";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import ReusableDropdown, { unifiedControlStyle } from "../components/ReusableDropdown";
import { getEnglishInitials } from "../components/UserAvatar";

// Import theme context for dark/light mode
import { ThemeContext } from "./theme";
// أضف هذا السطر مع باقي الـ imports
import { LanguageContext } from "./LanguageContext";
import ShareButton from "./sharebtn.js";


// Define API base URL
const API_URL = process.env.REACT_APP_BASE_URL;

// ===== RESPONSIVE HELPER FUNCTIONS =====
// ✅ getUserInitials - Uses shared English-only initials (NEVER Arabic)
const getUserInitials = (name, englishName = null) => {
  return getEnglishInitials(name, englishName, "US");
};
// Get responsive breakpoints
const getScreenSize = () => {
  const width = window.innerWidth;
  if (width <= 480) return "xs"; // Extra small mobile
  if (width <= 768) return "sm"; // Small mobile/large mobile
  if (width <= 1024) return "md"; // Tablet
  if (width <= 1366) return "lg"; // Small laptop
  if (width <= 1920) return "xl"; // Large laptop
  return "xxl"; // Desktop
};

// Get responsive spacing based on screen size
const getResponsiveSpacing = (screenSize) => {
  const spacingMap = {
    xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
    sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
    md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
    lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
    xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
    xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
  };
  return spacingMap[screenSize] || spacingMap.lg;
};

// Get responsive font sizes
const getResponsiveFontSizes = (screenSize) => {
  const fontMap = {
    xs: {
      title: "1.25rem",
      subtitle: "0.875rem",
      body: "0.75rem",
      small: "0.6875rem",
      tiny: "0.625rem",
    },
    sm: {
      title: "1.5rem",
      subtitle: "1rem",
      body: "0.875rem",
      small: "0.75rem",
      tiny: "0.6875rem",
    },
    md: {
      title: "1.75rem",
      subtitle: "1.125rem",
      body: "0.9375rem",
      small: "0.8125rem",
      tiny: "0.75rem",
    },
    lg: {
      title: "2rem",
      subtitle: "1.25rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xl: {
      title: "2.25rem",
      subtitle: "1.375rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xxl: {
      title: "2.5rem",
      subtitle: "1.5rem",
      body: "1.125rem",
      small: "1rem",
      tiny: "0.875rem",
    },
  };
  return fontMap[screenSize] || fontMap.lg;
};


// Get responsive grid columns
const getResponsiveGrid = (screenSize) => {
  const gridMap = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
    xxl: 6,
  };
  return gridMap[screenSize] || 4;
};

// Main Employee Management component
function EmployeeManagementManager({ onCountsUpdate, onEmployeeCountUpdate } = {}) {
  // Get theme context for styling
  const themeContext = useContext(ThemeContext);
  const languageContext = useContext(LanguageContext);

  const theme = themeContext?.theme || 'light';
  const language = languageContext?.language || 'en';
  const { profile } = useProfile();

  const [employeeProfilePics, setEmployeeProfilePics] = useState({});
  const [loadingPics, setLoadingPics] = useState(new Set());
  const picCacheRef = useRef(new Map());

  const isDarkMode = theme === "dark";

  // Debug log
  console.log('EmployeeManagementManager rendered', { theme, language });

  // أضف هذا الـ object بعد كل الـ useState
  const text = {
    en: {
      // Header
      title: "Employee Management System",
      subtitle: "Comprehensive management of employee data and status tracking",

      // Buttons
      addEmployee: "Add New Employee",
      add: "Add",
      export: "Export",
      share: "Share",
      archive: "View Archive",
      cancel: "Cancel",
      update: "Update Employee",
      save: "Save",
      delete: "Delete",
      restore: "Restore",
      restoreAll: "Restore All",
      deleteAll: "Delete All",
      updating: "Updating...",
      adding: "Adding...",

      // Table Headers
      employee: "Employee",
      department: "Department",
      joinDate: "Join Date",
      status: "Status",
      actions: "Actions",
      archivedDate: "Archived Date",
      company: "Company",

      // Departments
      infotech: "Information Technology",
      humanResources: "Human Resources",
      finance: "Finance",
      marketing: "Marketing",
      operations: "Operations",
      sales: "Sales",
      customerService: "Customer Service",

      // Filters
      searchEmployee: "Search Employee",
      allDepartments: "All Departments",
      allStatuses: "All Statuses",
      allCompanies: "All Companies",
      searchDepartment: "Search Department",
      selectStatus: "Select Status",

      // Stats
      totalEmployees: "Total Employees",
      activeEmployees: "Active Employees",
      onLeave: "On Leave",
      archived: "Archived",
      newEmployees: "New Employees",
      total: "Total",
      active: "Active",
      currentlyWorking: "Currently working",
      currentlyOnLeave: "Currently on leave",
      archivedEmployees: "Archived Employees",
      joinedRecently: "Joined Recently",
      allRegisteredEmployees: "All registered employees",
      allEmployees: "All employees",

      // Status
      statusActive: "Active",
      statusOnLeave: "On Leave",
      statusInactive: "Inactive",
      statusArchived: "Archived",
      statusUnknown: "Unknown",

      // Messages
      loading: "Loading...",
      loadingEmployees: "Loading employees...",
      noEmployees: "No employees found",
      noArchivedEmployees: "No archived employees found",
      employeesArchivedMessage: "Employees that are archived will appear here",
      showAll: "Show All",
      showLess: "Show Less",
      more: "more",

      // Modals
      employeeDetails: "Employee Details",
      editEmployee: "Edit Employee",
      addNewEmployee: "Add New Employee",
      viewArchive: "Archived",
      archivedDescription:
        "Archived employees are safely stored here. You can restore them to active status or permanently delete them.",

      // Form Labels
      fullName: "Full Name",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      nationalId: "National ID",
      position: "Position",
      laborMode: "Labor Mode",
      companyName: "Company",
      clientCode: "Client Code",
      statusLabel: "Status",
      salaryLabel: "Salary",
      dateOfBirth: "Date of Birth",
      gender: "Gender",
      maritalStatus: "Marital Status",
      nationality: "Nationality",
      city: "City",
      country: "Country",
      address: "Address",
      emergencyPhone: "Emergency Phone",

      // Form Placeholders
      enterFullName: "Enter full name",
      enterEmail: "Enter email address",
      enterPhone: "Enter phone number",
      enterNationalId: "Enter national ID",
      enterPosition: "Enter position",
      enterCompany: "Enter company name",
      enterClientCode: "Enter client code",
      enterSalary: "Enter salary amount",
      selectGender: "Select Gender",
      selectMaritalStatus: "Select Status",
      enterNationality: "Enter nationality",
      enterCity: "Enter city",
      enterCountry: "Enter country",
      enterAddress: "Enter full address",
      searchNameIdEmail: "Name, ID, email...",
      searchNameId: "Name, ID...",

      // Form Sections
      basicInformation: "Basic Information",
      workInformation: "Work Information",
      personalInformation: "Personal Information",
      contactInformation: "Contact Information",
      educationInformation: "Education Information",
      insuranceInformation: "Insurance Information",
      systemInformation: "System Information",

      // Gender Options
      male: "Male",
      female: "Female",

      // Marital Status Options
      single: "Single",
      married: "Married",
      divorced: "Divorced",
      widowed: "Widowed",

      // Labor Mode Options
      fullTime: "Full-time",
      partTime: "Part-time",
      contract: "Contract",
      temporary: "Temporary",
      freelance: "Freelance",

      // Info Labels
      email: "Email",
      phone: "Phone",
      employeeId: "Employee ID",
      hiringDate: "Hiring Date",
      workingExperience: "Working Experience",
      contractDate: "Contract Date",
      contractExpiration: "Contract Expiration",
      termOfContract: "Term of Contract",
      insuranceStatus: "Insurance Status",
      insuranceNumber: "Insurance Number",
      insuranceSalary: "Insurance Salary",
      insuranceStartDate: "Insurance Start Date",
      monthsForInsurance: "Months for Insurance",
      graduateInstitution: "Graduate Institution",
      major: "Major",
      graduationYear: "Graduation Year",
      createdAt: "Created At",
      lastUpdated: "Last Updated",
      years: "years",
      months: "months",

      // Action Buttons
      viewDetails: "View Details",
      deleteEmployee: "Delete Employee",
      archiveEmployee: "Archive Employee",
      restoreEmployee: "Restore Employee",
      permanentlyDelete: "Permanently Delete",

      // Confirmation Messages
      confirmDelete: "Are you sure you want to delete this employee?",
      confirmArchive: "Are you sure you want to archive this employee?",
      confirmRestore: "Are you sure you want to restore this employee?",
      confirmPermanentDelete:
        "Are you sure you want to permanently delete this employee? This action cannot be undone.",
      confirmRestoreAll: "Are you sure you want to restore all",
      archivedEmployeesQuestion: "archived employees?",
      confirmDeleteAll: "Are you sure you want to permanently delete all",
      cannotBeUndone: "This action cannot be undone.",

      // Success Messages
      employeeUpdated: "Employee updated successfully!",
      employeeAdded: "Employee added successfully!",
      employeeDeleted: "Employee deleted successfully!",
      employeeArchived: "Employee archived successfully!",
      employeeRestored: "Employee restored successfully!",
      allEmployeesDeleted: "All archived employees permanently deleted!",
      exportSuccess: "Exported",
      filteredEmployees: "filtered employees successfully!",
      exportSuccessCSV: "filtered employees as CSV!",

      // Error Messages
      nameRequired: "Name is required",
      emailRequired: "Email is required",
      emailInvalid: "Email is invalid",
      departmentRequired: "Department is required",
      positionRequired: "Position is required",
      failedToFetch: "Failed to fetch employee",
      failedToUpdate: "Failed to update employee",
      failedToAdd: "Failed to add employee",
      failedToDelete: "Failed to delete employee",
      failedToArchive: "Failed to archive employee",
      failedToRestore: "Failed to restore employee",
      failedToExport: "Failed to export employees data",
      serverError: "Server error during import",
      importFailed: "Import failed:",
      Status: "Status",

      // Archive Stats
      totalArchived: "Total Archived",
      archivedThisMonth: "Archived This Month",
      archivedEmployee: "archived employee",

      // Import Messages
      imported: "Imported",
      employeesSuccessfully: "employees successfully.",
      failedRows: "Failed rows:",
      row: "Row",

      // List Labels
      employeeList: "Employee List",
      employeesCount: "employees",
      employees: "Employees",

      viewDocuments: "View Documents",
      documents: "Documents",
      noDocuments: "No documents found",
      download: "Download",
      documentChecklist: "Document Checklist",
      photos: "Photos",
      birthForm: "Birth Certificate",
      graduation: "Graduation Certificate",
      idCopy: "ID Copy",
      criminalRecord: "Criminal Record",
      workCertificate: "Work Certificate",
      form111: "Form 111",
      updatingDocuments: "Updating documents...",
      documentsUpdated: "Documents updated successfully!",
      failedToUpdateDocuments: "Failed to update documents",

      // Not Available
      na: "N/A",
    },
    ar: {
      // Header
      title: "نظام إدارة الموظفين",
      subtitle: "إدارة شاملة لبيانات الموظفين وتتبع الحالة",

      // Buttons
      addEmployee: "إضافة موظف جديد",
      add: "إضافة",
      export: " تصدير إكسل",
      share: "مشاركة",
      archive: "عرض الأرشيف",
      cancel: "إلغاء",
      update: "تحديث الموظف",
      save: "حفظ",
      delete: "حذف",
      restore: "استعادة",
      restoreAll: "استعادة الكل",
      deleteAll: "حذف الكل",
      updating: "جاري التحديث...",
      adding: "جاري الإضافة...",
      // Table Headers
      employee: "الموظف",
      department: "القسم",
      salary: "الراتب",
      joinDate: "تاريخ التعيين",
      status: "الحالة",
      actions: "الإجراءات",
      archivedDate: "تاريخ الأرشفة",
      company: "الشركة",

      // Departments
      infotech: "تكنولوجيا المعلومات",
      humanResources: "الموارد البشرية",
      finance: "المالية",
      marketing: "التسويق",
      operations: "العمليات",
      sales: "المبيعات",
      customerService: "خدمة العملاء",

      // Filters
      searchEmployee: "بحث عن موظف",
      allDepartments: "جميع الأقسام",
      Status: "الحالة",
      allStatuses: "جميع الحالات",
      allCompanies: "جميع الشركات",
      searchDepartment: "اختر القسم",
      selectStatus: "اختر الحالة",

      // Stats
      totalEmployees: "إجمالي الموظفين",
      activeEmployees: "الموظفون النشطون",
      onLeave: "في إجازة",
      archived: "المؤرشف",
      newEmployees: "موظفون جدد",
      total: "الإجمالي",
      active: "نشط",
      currentlyWorking: "يعملون حالياً",
      currentlyOnLeave: "في إجازة حالياً",
      archivedEmployees: "الموظفون المؤرشفون",
      joinedRecently: "انضموا مؤخراً",
      allRegisteredEmployees: "جميع الموظفين المسجلين",
      allEmployees: "جميع الموظفين",

      // Status
      statusActive: "نشط",
      statusOnLeave: "في إجازة",
      statusInactive: "غير نشط",
      statusArchived: "مؤرشف",
      statusUnknown: "غير معروف",

      // Messages
      loading: "جاري التحميل...",
      loadingEmployees: "جاري تحميل الموظفين...",
      noEmployees: "لا يوجد موظفون",
      noArchivedEmployees: "لا يوجد موظفون مؤرشفون",
      employeesArchivedMessage: "الموظفون المؤرشفون سيظهرون هنا",
      showAll: "عرض الكل",
      showLess: "عرض أقل",
      more: "المزيد",

      // Modals
      employeeDetails: "تفاصيل الموظف",
      editEmployee: "تعديل الموظف",
      addNewEmployee: "إضافة موظف جديد",
      viewArchive: "الموظفون المؤرشفون",
      archivedDescription:
        "الموظفون المؤرشفون محفوظون هنا بأمان. يمكنك استعادتهم إلى الحالة النشطة أو حذفهم نهائياً.",

      // Form Labels
      fullName: "الاسم الكامل",
      emailAddress: "البريد الإلكتروني",
      phoneNumber: "رقم الهاتف",
      nationalId: "الرقم القومي",
      position: "المنصب",
      laborMode: "نظام العمل",
      companyName: "الشركة",
      clientCode: "كود العميل",
      statusLabel: "الحالة",
      salaryLabel: "الراتب",
      dateOfBirth: "تاريخ الميلاد",
      gender: "النوع",
      maritalStatus: "الحالة الاجتماعية",
      nationality: "الجنسية",
      city: "المدينة",
      country: "الدولة",
      address: "العنوان",
      emergencyPhone: "هاتف الطوارئ",

      // Form Placeholders
      enterFullName: "أدخل الاسم الكامل",
      enterEmail: "أدخل البريد الإلكتروني",
      enterPhone: "أدخل رقم الهاتف",
      enterNationalId: "أدخل الرقم القومي",
      enterPosition: "أدخل المنصب",
      enterCompany: "أدخل اسم الشركة",
      enterClientCode: "أدخل كود العميل",
      enterSalary: "أدخل قيمة الراتب",
      selectGender: "اختر النوع",
      selectMaritalStatus: "اختر الحالة",
      enterNationality: "أدخل الجنسية",
      enterCity: "أدخل المدينة",
      enterCountry: "أدخل الدولة",
      enterAddress: "أدخل العنوان الكامل",
      searchNameIdEmail: "الاسم، الرقم، البريد...",
      searchNameId: "الاسم، الرقم...",

      // Form Sections
      basicInformation: "المعلومات الأساسية",
      workInformation: "معلومات العمل",
      personalInformation: "المعلومات الشخصية",
      contactInformation: "معلومات الاتصال",
      educationInformation: "المعلومات التعليمية",
      insuranceInformation: "معلومات التأمين",
      systemInformation: "معلومات النظام",

      // Gender Options
      male: "ذكر",
      female: "أنثى",

      // Marital Status Options
      single: "أعزب",
      married: "متزوج",
      divorced: "مطلق",
      widowed: "أرمل",

      // Labor Mode Options
      fullTime: "دوام كامل",
      partTime: "دوام جزئي",
      contract: "عقد",
      temporary: "مؤقت",
      freelance: "عمل حر",

      // Info Labels
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      employeeId: "رقم الموظف",
      hiringDate: "تاريخ التعيين",
      netSalary: "صافي الراتب",
      workingExperience: "الخبرة العملية",
      contractDate: "تاريخ العقد",
      contractExpiration: "انتهاء العقد",
      termOfContract: "مدة العقد",
      insuranceStatus: "حالة التأمين",
      insuranceNumber: "رقم التأمين",
      insuranceSalary: "راتب التأمين",
      insuranceStartDate: "تاريخ بدء التأمين",
      monthsForInsurance: "أشهر التأمين",
      graduateInstitution: "المؤسسة التعليمية",
      major: "التخصص",
      graduationYear: "سنة التخرج",
      createdAt: "تاريخ الإنشاء",
      lastUpdated: "آخر تحديث",
      years: "سنوات",
      months: "أشهر",

      // Action Buttons
      viewDetails: "عرض التفاصيل",
      deleteEmployee: "حذف الموظف",
      archiveEmployee: "أرشفة الموظف",
      restoreEmployee: "استعادة الموظف",
      permanentlyDelete: "حذف نهائي",

      // Confirmation Messages
      confirmDelete: "هل أنت متأكد من حذف هذا الموظف؟",
      confirmArchive: "هل أنت متأكد من أرشفة هذا الموظف؟",
      confirmRestore: "هل أنت متأكد من استعادة هذا الموظف؟",
      confirmPermanentDelete:
        "هل أنت متأكد من الحذف النهائي لهذا الموظف؟ لا يمكن التراجع عن هذا الإجراء.",
      confirmRestoreAll: "هل أنت متأكد من استعادة كل",
      archivedEmployeesQuestion: "الموظفين المؤرشفين؟",
      confirmDeleteAll: "هل أنت متأكد من الحذف النهائي لكل",
      cannotBeUndone: "لا يمكن التراجع عن هذا الإجراء.",

      // Success Messages
      employeeUpdated: "تم تحديث الموظف بنجاح!",
      employeeAdded: "تم إضافة الموظف بنجاح!",
      employeeDeleted: "تم حذف الموظف بنجاح!",
      employeeArchived: "تم أرشفة الموظف بنجاح!",
      employeeRestored: "تم استعادة الموظف بنجاح!",
      allEmployeesDeleted: "تم حذف جميع الموظفين المؤرشفين نهائياً!",
      exportSuccess: "تم تصدير",
      filteredEmployees: "الموظفين المفلترين بنجاح!",
      exportSuccessCSV: "الموظفين المفلترين كملف CSV!",

      // Error Messages
      nameRequired: "الاسم مطلوب",
      emailRequired: "البريد الإلكتروني مطلوب",
      emailInvalid: "البريد الإلكتروني غير صحيح",
      departmentRequired: "القسم مطلوب",
      positionRequired: "المنصب مطلوب",
      failedToFetch: "فشل في جلب بيانات الموظف",
      failedToUpdate: "فشل في تحديث الموظف",
      failedToAdd: "فشل في إضافة الموظف",
      failedToDelete: "فشل في حذف الموظف",
      failedToArchive: "فشل في أرشفة الموظف",
      failedToRestore: "فشل في استعادة الموظف",
      failedToExport: "فشل في تصدير بيانات الموظفين",

      // Archive Stats
      totalArchived: "إجمالي المؤرشف",
      archivedThisMonth: "المؤرشف هذا الشهر",
      archivedEmployee: "موظف مؤرشف",

      // List Labels
      employeeList: "قائمة الموظفين",
      employeesCount: "موظف",
      employees: "الموظفين",

      viewDocuments: "عرض المستندات",
      documents: "المستندات",
      noDocuments: "لا توجد مستندات",
      download: "تحميل",
      documentChecklist: "قائمة فحص المستندات",
      photos: "الصور",
      birthForm: "شهادة الميلاد",
      graduation: "شهادة التخرج",
      idCopy: "نسخة البطاقة",
      criminalRecord: "صحيفة الحالة الجنائية",
      workCertificate: "شهادة العمل",
      form111: "نموذج 111",
      updatingDocuments: "جاري تحديث المستندات...",
      documentsUpdated: "تم تحديث المستندات بنجاح!",
      failedToUpdateDocuments: "فشل في تحديث المستندات",

      // Not Available
      na: "غير متاح",
    },
  };
  // ===== RESPONSIVE STATE MANAGEMENT =====
  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
  const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));
  const [gridCols, setGridCols] = useState(getResponsiveGrid(screenSize));
  const [expandedEmployees, setExpandedEmployees] = useState(() => {
    const myCode = localStorage.getItem("infinityCode");
    return myCode ? new Set([myCode]) : new Set();
  });
  // Update responsive values on window resize
  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      setScreenSize(newScreenSize);
      setSpacing(getResponsiveSpacing(newScreenSize));
      setFonts(getResponsiveFontSizes(newScreenSize));
      setGridCols(getResponsiveGrid(newScreenSize));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ===== EXISTING STATE MANAGEMENT =====
  // State for employee count
  const [employeeCount, setEmployeeCount] = useState(0);
  // State for archived employee count
  const [archivedCount, setArchivedCount] = useState(0);

  // State for different employee status counts
  const [counts, setCounts] = useState({ Active: 0, OnLeave: 0, Recent: 0 });

  // Modal visibility states
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [filterCompanySearch, setFilterCompanySearch] = useState("");

  // Selected employee for view/edit operations
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterStatusSearch, setFilterStatusSearch] = useState("");

  // Loading and message states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Employee data states
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);


  // Add these states with your other useState declarations
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false); // Track if saving is in progress
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [documentChecklist, setDocumentChecklist] = useState({
    photosCount: false,
    birthCertificateCopy: false,
    gradCertificateOriginal: false,
    idCopy: false,
    noCrimeCertificate: false,
    laborLetterOriginal: false,
    medicalCheckForm111: false,
  });

  // Effect to fetch employee count on component mount
  useEffect(() => {
    fetchEmployeeCount();
    fetchArchivedEmployees();
  }, []);

  // Effect to fetch employee counts by status
  useEffect(() => {
    fetchEmployeeCounts();
  }, []);

  // Effect to fetch all employees
  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
  }, []);

  // Share report handler
  const handleShareReport = async (report, withAttachment, emailForm) => {
    console.log("Sharing report:", report);
    console.log("With attachment?", withAttachment);
    console.log("Email form data:", emailForm);
  };

  // Add this helper function with better error handling
  const flattenHierarchy = (node, level = 0, result = [], visited = new Set(), parentExpanded = true) => {
    if (!node) return result;

    if (visited.has(node.infinityCode)) {
      console.warn(`⚠️ Circular reference detected: ${node.infinityCode}`);
      return result;
    }

    visited.add(node.infinityCode);

    // ✅ أضف الـ node بس لو الـ parent مفتوح (أو هو root)
    if (parentExpanded) {
      result.push({
        ...node,
        level,
        isExpanded: expandedEmployees.has(node.infinityCode),
        hasChildren: node.children && node.children.length > 0,
      });
    }

    // ✅ أظهر children بس لو الـ node ده مفتوح
    const shouldShowChildren = expandedEmployees.has(node.infinityCode);

    if (shouldShowChildren && node.children && node.children.length > 0) {
      node.children.forEach(child => {
        flattenHierarchy(child, level + 1, result, new Set(visited), true);
      });
    }

    return result;
  };

  // Add toggle function
  const toggleEmployeeExpand = (infinityCode) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(infinityCode)) {
        newSet.delete(infinityCode);
      } else {
        newSet.add(infinityCode);
      }
      return newSet;
    });
  };

  // Function to fetch total employee count (filtered by client name)
  const fetchEmployeeCount = async () => {
    try {
      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // Call the grouped API
      console.log("📡 Fetching employee counts from all clients");
      const response = await apiFetch(`${API_URL}/employees/clients/count`, { method: "GET" });
      const data = await response.json(); // Array of { name, code, count }

      console.log(`✅ Received count data for ${data.length} clients`);

      let totalCount;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter data for specific client only
        console.log(`🔍 Filtering count for client: ${clientName}`);
        const filteredData = data.filter(
          (client) => client.name?.toLowerCase() === clientName.toLowerCase()
        );

        // Calculate total count for the selected client
        totalCount = filteredData.reduce(
          (sum, client) => sum + client.count,
          0
        );
        console.log(`✅ Total count for ${clientName}: ${totalCount}`);
      } else {
        // Calculate total count for all clients (Infinity or no client set)
        console.log("🔍 Calculating total count for all clients");
        totalCount = data.reduce((sum, client) => sum + client.count, 0);
        console.log(`✅ Total count for all clients: ${totalCount}`);
      }

      // Update total employee count
      setEmployeeCount(totalCount);
      if (typeof onEmployeeCountUpdate === "function") {
        onEmployeeCountUpdate(totalCount);
      }
      console.log("✅ Employee count updated successfully!");
    } catch (err) {
      console.error("❌ Error fetching employee count:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError("Failed to fetch employee count");
    }
  };

  const fetchEmployeeCounts = async () => {
    try {
      console.log("🔍 Starting fetchEmployeeCounts...");
      console.log("📍 API_URL:", API_URL);

      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      console.log(`📍 Client Name: ${clientName}`);

      // Fetch active employees by client
      console.log(
        "📡 Fetching Active counts from:",
        `${API_URL}/employees/clients/Active/count`
      );
      const activeRes = await apiFetch(
        `${API_URL}/employees/clients/Active/count`,
        { method: "GET" }
      );
      console.log("✅ Active Response Status:", activeRes.status);
      console.log(
        "✅ Active Response Data:",
        JSON.stringify(activeRes.data, null, 2)
      );

      // Fetch on-leave employees by client
      console.log(
        "📡 Fetching OnLeave counts from:",
        `${API_URL}/employees/clients/OnLeave/count`
      );
      const onLeaveRes = await apiFetch(
        `${API_URL}/employees/clients/OnLeave/count`,
        { method: "GET" }
      );
      console.log("✅ OnLeave Response Status:", onLeaveRes.status);
      console.log(
        "✅ OnLeave Response Data:",
        JSON.stringify(onLeaveRes.data, null, 2)
      );

      const activeData = await activeRes.json();
      const onLeaveData = await onLeaveRes.json();

      // Verify data is array
      console.log(
        "🔍 Active data type:",
        Array.isArray(activeData) ? "Array" : typeof activeData
      );
      console.log(
        "🔍 OnLeave data type:",
        Array.isArray(onLeaveData) ? "Array" : typeof onLeaveData
      );

      if (!Array.isArray(activeData)) {
        console.error("❌ Active data is NOT an array:", activeData);
        throw new Error("Active data is not an array");
      }
      if (!Array.isArray(onLeaveData)) {
        console.error("❌ OnLeave data is NOT an array:", onLeaveData);
        throw new Error("OnLeave data is not an array");
      }

      console.log("📊 Active data length:", activeData.length);
      console.log("📊 OnLeave data length:", onLeaveData.length);

      let clientActiveItems;
      let clientOnLeaveItems;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(`🔍 Filtering counts for client: ${clientName}`);

        clientActiveItems = activeData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking active item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });

        clientOnLeaveItems = onLeaveData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking onLeave item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });
      } else {
        // Use all data for Infinity or when no client is set
        console.log("🔍 Using counts for all clients (Infinity)");
        clientActiveItems = activeData;
        clientOnLeaveItems = onLeaveData;
      }

      console.log(
        "✅ Filtered Active Items:",
        JSON.stringify(clientActiveItems, null, 2)
      );
      console.log(
        "✅ Filtered OnLeave Items:",
        JSON.stringify(clientOnLeaveItems, null, 2)
      );

      // Calculate totals
      const activeCount = clientActiveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding active count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      const onLeaveCount = clientOnLeaveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding onLeave count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      console.log(`📈 FINAL COUNTS:`);
      console.log(`   - Active: ${activeCount}`);
      console.log(`   - OnLeave: ${onLeaveCount}`);
      console.log(`   - Total: ${activeCount + onLeaveCount}`);

      // Update state
      const updatedCounts = {
        Active: activeCount,
        OnLeave: onLeaveCount,
        Recent: 0,
        Total: activeCount + onLeaveCount,
      };

      setCounts(updatedCounts);
      if (typeof onCountsUpdate === "function") {
        onCountsUpdate(updatedCounts);
      }

      console.log("✅ State updated successfully!");
    } catch (err) {
      console.error("❌ ERROR in fetchEmployeeCounts:");
      console.error("   - Message:", err.message);
      console.error("   - Response Status:", err.response?.status);
      console.error("   - Response Data:", err.response?.data);
      console.error("   - Full Error:", err);
      setError("Failed to fetch employee statistics");
    }
  };

  // Replace your existing fetchEmployees function with this:

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const myInfinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");

      let response;

      if (clientName && clientName.toLowerCase() !== "infinity") {
        console.log(`📡 Fetching hierarchy for client: ${clientName}, manager: ${myInfinityCode}`);

        response = await apiFetch(
          `${API_URL}/employees/clients/${clientName}/hierarchy?managerInfinityCode=${myInfinityCode}`,
          { method: "GET" }
        );
      } else {
        console.log("📡 Fetching all employees");
        response = await apiFetch(`${API_URL}/employees`, { method: "GET" });
      }

      let employeesData;

      // Check if response contains hierarchy structure
      const json = await response.json();
      if (json.hierarchy) {
        // Flatten the hierarchy for table display
        employeesData = flattenHierarchy(json.hierarchy[0]);
      } else {
        employeesData = json.employees || json;
      }

      console.log(`✅ Received ${employeesData.length} employees`);

      // Map employees with ALL fields
      const mappedEmployees = employeesData.map((emp) => ({
        id: emp.infinityCode || emp.id,
        name: language === "ar" ? (emp.arabicName || emp.sr || "N/A") : (emp.sr || emp.arabicName || "N/A"),
        laborMode: emp.laborMode || "N/A",
        department: emp.employeeType || "N/A",
        email: emp.email || "N/A",
        phone: emp.phoneNumber || "N/A",
        joinDate: emp.hiringDate,
        status: emp.employeeState || text[language].statusActive,
        company: emp.clientName || "N/A",
        level: emp.level || 0,
        hasChildren: emp.hasChildren || false,
        isExpanded: emp.isExpanded || false,
        avatar: (language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName))
          ? (language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName))[0].toUpperCase() +
          ((language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName)).split(" ")[1]?.[0]?.toUpperCase() || "")
          : "NA",
        fullEmployeeData: {
          // All fields except insuranceSalary and netSalary
          clientName: emp.clientName || "N/A",
          clientCode: emp.clientCode || "N/A",
          arabicName: emp.arabicName || "N/A",
          idRegion: emp.idRegion || "N/A",
          nationalId: emp.nationalId || "N/A",
          dateOfBirth: emp.dob || null,
          age: emp.age || "N/A",
          gender: emp.gender || "N/A",
          emergencyPhone: emp.emergencyPhoneNumber || "N/A",
          resignedDate: emp.resignedDate || null,
          resignReasons: emp.resignReasons || "N/A",
          beRegularDate: emp.beRegularDate || null,
          toBeRegular: emp.toBeRegular || "N/A",
          workingAgeYears: emp.workingAgeYears || 0,
          workingAgeMonths: emp.workingAgeMonths || 0,
          periodForBeingRegular: emp.periodForBeingRegular || "N/A",
          laborMode: emp.laborMode || "N/A",
          contractCompany: emp.contractCompany || "N/A",
          contractDate: emp.contractDate || null,
          termOfContract: emp.termOfContract || "N/A",
          contractExpiration: emp.contractExpiration || null,
          insuredBeginningDate: emp.insuredBeginningDate || null,
          insuranceStatus: emp.insuranceStatus || "N/A",
          monthsForInsurance: emp.monthsForInsurance || 0,
          insuranceNumber: emp.insuranceNumber || "N/A",
          // insuranceSalary: EXCLUDED
          address: emp.address || "N/A",
          arabicAddress: emp.arabicAddress || "N/A",
          city: emp.city || "N/A",
          country: emp.country || "N/A",
          religion: emp.religion || "N/A",
          maritalStatus: emp.maritalStatus || "N/A",
          nationality: emp.nationality || "N/A",
          graduateInstitution: emp.graduateInstitution || "N/A",
          major: emp.major || "N/A",
          graduationYear: emp.graduationYear || "N/A",
          idCopy: emp.idCopy ?? false,
          gradCertificateOriginal: emp.gradCertificateOriginal ?? false,
          birthCertificateCopy: emp.birthCertificateCopy ?? false,
          militaryServiceCertificate: emp.militaryServiceCertificate ?? false,
          laborLetterOriginal: emp.laborLetterOriginal ?? false,
          noCrimeCertificate: emp.noCrimeCertificate ?? false,
          photosCount: emp.photosCount ?? false,
          uniformSize: emp.uniformSize || "N/A",
          formNo6Original: emp.formNo6Original ?? false,
          medicalCheckForm111: emp.medicalCheckForm111 ?? false,
          didMedicalCheck: emp.didMedicalCheck || false,
          medicalReportOriginal: emp.medicalReportOriginal || false,
          lawyerCertificateCopy: emp.lawyerCertificateCopy || false,
          driversLicenseCopy: emp.driversLicenseCopy || false,
          testEngineerCertificate: emp.testEngineerCertificate || false,
          accountantCertificate: emp.accountantCertificate || false,
          militaryStatus: emp.militaryStatus || "N/A",
          healthCertificateOB: emp.healthCertificateOB || false,
          healthCertExpiryDate: emp.healthCertExpiryDate || null,
          healthCertStillValid: emp.healthCertStillValid || false,
          drivingLicenseDegree: emp.drivingLicenseDegree || "N/A",
          drivingLicenseExpiry: emp.drivingLicenseExpiry || null,
          drivingLicenseStillValid: emp.drivingLicenseStillValid || false,
          lastContractDate: emp.lastContractDate || null,
          lastContractTerm: emp.lastContractTerm || "N/A",
          lastContractExpiration: emp.lastContractExpiration || null,
          medicalInsuranceStage: emp.medicalInsuranceStage || "N/A",
          delivered: emp.delivered || false,
          medicalNumber: emp.medicalNumber || "N/A",
          // netSalary: EXCLUDED
          transportationAllowances: emp.transportationAllowances || 0,
          variableSalary: emp.variableSalary || 0,
          workingCompany: emp.workingCompany || "N/A",
          createdAt: emp.created_at || null,
          updatedAt: emp.updated_at || null,
          firstLineManagerName: emp.firstLineManagerName || "N/A",
          secondLineManagerName: emp.secondLineManagerName || "N/A",
          firstLineManagerInfinityCode: emp.firstLineManagerInfinityCode || null,
          secondLineManagerInfinityCode: emp.secondLineManagerInfinityCode || null,
        },
      }));

      setEmployees(mappedEmployees);
      // Reset pagination and compute availability
      console.log(`✅ Successfully mapped ${mappedEmployees.length} employees`);
    } catch (err) {
      console.error("❌ Failed to fetch employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      setLoading(false);
    }
  };



  // Re-fetch when expandedEmployees changes
  useEffect(() => {
    if (employees.length > 0) {
      fetchEmployees();
    }
  }, [expandedEmployees]);

  // Function to fetch unique companies for the current client
  const fetchCompanies = async () => {
    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      let response;

      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Fetch employees for specific client
        response = await apiFetch(`${API_URL}/employees/clients/${clientName}`, { method: "GET" });
      } else {
        // Fetch all employees
        response = await apiFetch(`${API_URL}/employees`, { method: "GET" });
      }
      const employeesData = await response.json();
      const list = employeesData.employees || employeesData;

      // Extract unique companies from workingCompany field
      const uniqueCompanies = [...new Set(
        list
          .map(emp => emp.workingCompany)
          .filter(company => company && company.trim() !== "")
      )].sort();

      setCompanies(uniqueCompanies);
      console.log(`✅ Found ${uniqueCompanies.length} unique companies`);
    } catch (err) {
      console.error("❌ Failed to fetch companies:", err);
    }
  };
  // Cleanup profile picture URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [employeeProfilePics]);

  // ✅ Batch fetch all profile pictures when employees load
  useEffect(() => {
    const fetchAllProfilePics = async () => {
      if (employees.length === 0) return;

      const missingPics = employees
        .filter(emp => !employeeProfilePics[emp.id] && !picCacheRef.current.has(emp.id))
        .map(emp => emp.id);

      if (missingPics.length === 0) return;

      console.log(`📸 Fetching ${missingPics.length} profile pictures...`);

      const BATCH_SIZE = 5;
      const newPics = {};

      for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
        const batch = missingPics.slice(i, i + BATCH_SIZE);

        const results = await Promise.all(
          batch.map(async (id) => {
            try {
              const url = await fetchEmployeeProfilePic(id);
              return { id, url };
            } catch (err) {
              // Only log non-404 errors (404 means no profile pic, which is normal)
              if (err.response?.status !== 404 && err.message?.includes('404') === false) {
                console.error(`❌ Failed to load pic for ${id}:`, err.message);
              }
              return { id, url: null };
            }
          })
        );

        results.forEach(({ id, url }) => {
          if (url) {
            newPics[id] = url;
          }
        });

        if (i + BATCH_SIZE < missingPics.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (Object.keys(newPics).length > 0) {
        setEmployeeProfilePics(prev => ({ ...prev, ...newPics }));
        console.log(`✅ Loaded ${Object.keys(newPics).length} profile pictures!`);
      }
    };

    fetchAllProfilePics();
  }, [employees]);

  useEffect(() => {
    return () => {
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });

      picCacheRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });

      picCacheRef.current.clear();
    };
  }, []);

  const fetchEmployeeProfilePic = async (infinityCode) => {
    try {
      if (picCacheRef.current.has(infinityCode)) {
        return picCacheRef.current.get(infinityCode);
      }

      if (loadingPics.has(infinityCode)) {
        return null;
      }

      setLoadingPics(prev => new Set([...prev, infinityCode]));

      const response = await apiFetch(`${API_URL}/employees/pic/${infinityCode}`, { method: "GET" });

      // Handle 404 - employee has no profile picture
      if (response.status === 404) {
        setLoadingPics(prev => {
          const newSet = new Set(prev);
          newSet.delete(infinityCode);
          return newSet;
        });
        return null;
      }

      if (!response.ok) {
        throw new Error(`Pic fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);

        picCacheRef.current.set(infinityCode, url);

        setLoadingPics(prev => {
          const newSet = new Set(prev);
          newSet.delete(infinityCode);
          return newSet;
        });

        return url;
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error(`❌ Error loading pic for ${infinityCode}:`, err.message);
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    }
  };

  // Function to fetch archived employees
  const fetchArchivedEmployees = async () => {
    try {
      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // Make API call to get all archived employees
      console.log("📡 Fetching all archived employees");
      const response = await apiFetch(`${API_URL}/employees/archived`, { method: "GET" });
      const archivedJson = await response.json();
      const archivedData = archivedJson.employees || archivedJson;

      console.log(`✅ Received ${archivedData.length} archived employees`);

      // FIXED: Filter on frontend based on client name
      let filteredArchivedData = archivedData;

      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(
          `🔍 Filtering archived employees for client: ${clientName}`
        );
        filteredArchivedData = archivedData.filter(
          (emp) => emp.clientName?.toLowerCase() === clientName.toLowerCase()
        );
        console.log(
          `✅ Filtered to ${filteredArchivedData.length} archived employees for ${clientName}`
        );
      }

      // Map archived employees with proper formatting
      const mappedArchivedEmployees = filteredArchivedData.map((emp) => ({
        id: emp.infinityCode || emp.id,
        name: language === "ar" ? (emp.arabicName || emp.sr || "N/A") : (emp.sr || emp.arabicName || "N/A"),
        laborMode: emp.laborMode || "N/A",
        department: emp.employeeType || "N/A",
        email: emp.email || "N/A",
        phone: emp.phoneNumber || "N/A",
        salary: emp.netSalary || 0,
        joinDate: emp.hiringDate,
        status: "Archived", // All archived employees have status "Archived"
        company: emp.clientName || "N/A",
        archivedDate: emp.archivedDate || new Date().toISOString(), // Archive date
        // Generate avatar initials
        avatar: (language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName))
          ? (language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName))[0].toUpperCase() +
          ((language === "ar" ? (emp.arabicName || emp.sr) : (emp.sr || emp.arabicName)).split(" ")[1]?.[0]?.toUpperCase() || "")
          : "NA",
      }));

      // Update archived employees state
      setArchivedEmployees(mappedArchivedEmployees || []);
      // Update archived count
      setArchivedCount(mappedArchivedEmployees?.length || 0);

      console.log(
        `✅ Successfully mapped ${mappedArchivedEmployees.length} archived employees`
      );
    } catch (err) {
      // Log error - don't show error message as endpoint might not exist
      console.error("❌ Failed to fetch archived employees:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      // Set empty array if endpoint doesn't exist
      setArchivedEmployees([]);
      setArchivedCount(0);
    }
  };

  // Function to handle viewing employee details
  const handleViewEmployee = (employee) => {
    // Set selected employee
    setSelectedEmployee(employee);
    // Initialize document checklist from employee data
    const empData = employee.fullEmployeeData || {};
    setDocumentChecklist({
      photosCount: empData.photosCount || false,
      birthCertificateCopy: empData.birthCertificateCopy || false,
      gradCertificateOriginal: empData.gradCertificateOriginal || false,
      idCopy: empData.idCopy || false,
      noCrimeCertificate: empData.noCrimeCertificate || false,
      laborLetterOriginal: empData.laborLetterOriginal || false,
      medicalCheckForm111: empData.medicalCheckForm111 || false,
    });
    // Show view modal
    setShowViewEmployee(true);
  };

  // Function to handle editing employee
  const handleEditEmployee = (employee) => {
    // Set selected employee
    setSelectedEmployee(employee);
    // Show edit modal
    setShowEditEmployee(true);
  };

  // Add this function with your other handler functions
  const handleViewDocuments = async (employee) => {
    try {
      console.log('📄 Opening documents for employee:', employee);
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setHasUnsavedChanges(false); // Reset unsaved changes flag

      // Initialize document checklist from employee data
      const empData = employee.fullEmployeeData || {};
      console.log('📋 Employee document data:', empData);

      const checklistData = {
        photosCount: empData.photosCount === 1 || empData.photosCount === true, // Handle both int and boolean
        birthCertificateCopy: empData.birthCertificateCopy || false,
        gradCertificateOriginal: empData.gradCertificateOriginal || false,
        idCopy: empData.idCopy || false,
        noCrimeCertificate: empData.noCrimeCertificate || false,
        laborLetterOriginal: empData.laborLetterOriginal || false,
        medicalCheckForm111: empData.medicalCheckForm111 || false,
      };

      console.log('✅ Setting document checklist:', checklistData);
      setDocumentChecklist(checklistData);
      setShowDocumentsModal(true);
    } catch (err) {
      console.error("❌ Failed to load documents:", err);
      setError(text[language].failedToFetch + " documents");
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Handler to update document checklist
  // Update checkbox state without saving
  const handleDocumentCheckboxChange = (documentField, value) => {
    console.log(`📝 Checkbox changed: ${documentField} = ${value}`);

    // Update local state
    setDocumentChecklist(prev => ({
      ...prev,
      [documentField]: value
    }));

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  // Save all document changes to database
  const handleSaveDocuments = async () => {
    try {
      console.log(`💾 Saving all documents for employee ${selectedEmployeeDocuments?.id}`);

      // Set saving state
      setSavingDocument(true);

      // Prepare payload with all document fields - convert booleans to integers for photosCount
      const payload = {
        photosCount: documentChecklist.photosCount ? true : false,
        birthCertificateCopy: documentChecklist.birthCertificateCopy,
        gradCertificateOriginal: documentChecklist.gradCertificateOriginal,
        idCopy: documentChecklist.idCopy,
        noCrimeCertificate: documentChecklist.noCrimeCertificate,
        laborLetterOriginal: documentChecklist.laborLetterOriginal,
        medicalCheckForm111: documentChecklist.medicalCheckForm111,
      };

      console.log('🚀 Sending update to API:', payload);
      console.log('🌐 API URL:', `${API_URL}/employees/${selectedEmployeeDocuments.id}`);

      // Make API call to update employee documents
      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployeeDocuments.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      // Try to get error details from response
      let errorData;
      try {
        errorData = await response.json();
        console.log('📡 Response data:', errorData);
      } catch (e) {
        console.log('⚠️ Could not parse response as JSON');
      }

      if (!response.ok) {
        const errorMessage = errorData?.message || errorData?.error || `Server error: ${response.status}`;
        console.error('❌ Server error:', errorMessage);
        throw new Error(errorMessage);
      }

      console.log('✅ Documents saved successfully');

      // Update the employee data in state
      setEmployees(prev => prev.map(emp =>
        emp.id === selectedEmployeeDocuments.id
          ? { ...emp, fullEmployeeData: { ...emp.fullEmployeeData, ...payload } }
          : emp
      ));

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
      setSavingDocument(false);

    } catch (err) {
      console.error("❌ Failed to save documents:", err);
      console.error("❌ Error details:", err.message);
      setSavingDocument(false);
      setError(`${text[language].failedToUpdateDocuments}: ${err.message}`);
    }
  };


  // Helper function to generate CSV template
  const generateTemplateCSV = () => {
    const headers = [
      "SR.",
      "Client Name",
      "Client Code",
      "Infinity Code",
      "SR.",
      "Arabic Name",
      "ID Region",
      "Employee Type",
      "Location",
      "National ID",
      "DOB",
      "Age",
      "Gender",
      "Phone number",
      "Emergency Phone Number",
      "Hiring Date",
      "Resigned Date",
      "Resign Reasons",
      "Be-Regular Date",
      "To Be Regular Or Not",
      "working age(year)",
      "working age(month)",
      "Period  for being regular",
      "labor mode",
      "Contract Company",
      "Contract Date",
      "Term of contract",
      "Contract Expiration",
      "beginning date for the insured",
      "Insurance Status",
      "months for insurance",
      "Insurance Number",
      "Insurance Salary",
      "Email",
      "Address",
      "City",
      "Country",
      "Religion",
      "Marital Status",
      "Nationality",
      "Graduate institutions and universities",
      "Major",
      "Graduation year",
      "ID Copy",
      "original copy of Graduation certificate",
      "Original and copy of birth certificate",
      "Original military service certificate",
      "Original labor letter",
      "No crime certificate",
      "6 Photos",
      "uniform size",
      "original copy of Form No.6",
      "Medical Check Form111",
      "Did Medical Check or Not",
      "Original copy of medical examination report",
      "Copy of lawyer's certificate",
      "Copy of driver's license",
      "Copy of Test engineer's certificate",
      "Copy of accountant certificate",
      "Militry Status",
      "Health Certificate For Office Boys",
      "Health Certificate Expiry Date",
      "Health Certificate Still Available or not",
      "Drive license Degree",
      "Driving License Expiry date",
      "Drive.License Still Available",
      "Date of last contract",
      "Term of last contract",
      "Expiration of last contract",
      "Medical Insurance Stage",
      "Delivered",
      "Medical Number",
      "Employee state"
    ];
    // Create 5 empty rows for template
    const rows = Array(5).fill(headers.map(() => ""));

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };


  // Function to handle updating employee data
  const handleUpdateEmployee = async (employeeData) => {
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        employeeType: employeeData.department,
        laborMode: employeeData.laborMode,
        clientName: employeeData.company,
        employeeState: employeeData.status,
        netSalary: employeeData.salary ? parseFloat(employeeData.salary) : 0,
        nationalId: employeeData.nationalId || "",
        address: employeeData.address || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        dob: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth).toISOString()
          : null,
      };

      // Make API call to update employee
      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployee.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const updated = await response.json();

      // Update employee in local state
      const updatedEmployees = employees.map(
        (emp) =>
          emp.id === selectedEmployee.id
            ? {
              // Spread existing employee data
              ...emp,
              // Update with new data from response
              name: language === "ar" ? (updated.arabicName || updated.sr) : (updated.sr || updated.arabicName),
              email: updated.email,
              phone: updated.phoneNumber,
              department: updated.employeeType,
              laborMode: updated.laborMode,
              company: updated.clientName,
              status: updated.employeeState,
              salary: updated.netSalary,
              // Update avatar with new name initials
              avatar: (language === "ar" ? (updated.arabicName || updated.sr) : (updated.sr || updated.arabicName))
                ? (language === "ar" ? (updated.arabicName || updated.sr) : (updated.sr || updated.arabicName))[0].toUpperCase() +
                ((language === "ar" ? (updated.arabicName || updated.sr) : (updated.sr || updated.arabicName)).split(" ")[1]?.[0]?.toUpperCase() || "")
                : "NA",
            }
            : emp // Return unchanged employee if not the one being updated
      );

      // Update employees state
      setEmployees(updatedEmployees);
      // Close edit modal
      setShowEditEmployee(false);
      // Clear selected employee
      setSelectedEmployee(null);
      // Show success message
      setSuccess(text[language].employeeUpdated);
    } catch (err) {
      // Log error and show error message
      console.error("Failed to update employee:", err);
      setError(err.response?.data?.error || text[language].failedToUpdate);
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle adding new employee
  const handleAddEmployee = async (employeeData) => {
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        location: employeeData.address,
        employeeType: employeeData.position,
        laborMode: employeeData.laborMode,
        contractCompany: employeeData.company,
        employeeState: employeeData.status,
        netSalary: employeeData.salary ? parseFloat(employeeData.salary) : 0,
        hiringDate: new Date().toISOString(), // Set current date as hiring date
        nationalId: employeeData.nationalId || "",
        address: employeeData.address || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        dob: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth).toISOString()
          : null,
      };

      // Make API call to add employee
      const response = await apiFetch(`${API_URL}/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const created = await response.json();

      // Create new employee object from response
      const newEmployee = {
        id: created.infinityCode || created.id,
        name: created.sr,
        position: created.employeeType || "",
        department: created.location || "",
        email: created.email,
        phone: created.phoneNumber,
        salary: created.netSalary,
        joinDate: created.hiringDate,
        status: created.employeeState,
        company: created.contractCompany,
        // Generate avatar initials
        avatar: response.data.sr
          ? response.data.sr[0].toUpperCase() +
          (response.data.sr.split(" ")[1]?.[0]?.toUpperCase() || "")
          : "NA",
      };

      // Add new employee to employees array
      setEmployees([...employees, newEmployee]);
      // Close add modal
      setShowAddEmployee(false);
      // Show success message
      setSuccess(text[language].employeeAdded);

      // Refresh counts
      fetchEmployeeCount();
      fetchEmployeeCounts();
    } catch (err) {
      // Log error and show error message
      console.error("Failed to add employee:", err);
      setError(err.response?.data?.error || text[language].failedToAdd);
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle deleting employee
  const handleDeleteEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmDelete)) {
      try {
        // Set loading state
        setLoading(true);
        // Make API call to delete employee
        await apiFetch(`${API_URL}/employees/${employeeId}`, { method: "DELETE" });

        // Remove employee from local state
        setEmployees(employees.filter((emp) => emp.id !== employeeId));
        // Show success message
        setSuccess(text[language].employeeDeleted);

        // Refresh counts
        fetchEmployeeCount();
        fetchEmployeeCounts();
      } catch (err) {
        // Log error and show error message
        console.error("Failed to delete employee:", err);
        setError(text[language].failedToDelete);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };

  // Function to handle archiving employee
  const handleArchiveEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmArchive)) {
      try {
        // Set loading state
        setLoading(true);

        // Find the employee to archive
        const employeeToArchive = employees.find(
          (emp) => emp.id === employeeId
        );

        // Add archived date and update status
        const archivedEmployee = {
          ...employeeToArchive,
          archivedDate: new Date().toISOString(),
          status: "Archived",
        };

        // Send to archive endpoint
        await apiFetch(`${API_URL}/employees/archive`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          employeeId: employeeId,
          archivedData: archivedEmployee,
        });

        // Remove from active employees
        await apiFetch(`${API_URL}/employees/${employeeId}`, { method: "DELETE" });

        // Update local states
        setEmployees(employees.filter((emp) => emp.id !== employeeId));
        setArchivedEmployees([...archivedEmployees, archivedEmployee]);
        setArchivedCount(archivedCount + 1);
        setSuccess(text[language].employeeArchived);

        // Refresh counts
        fetchEmployeeCount();
        fetchEmployeeCounts();
      } catch (err) {
        // Log error and show error message
        console.error("Failed to archive employee:", err);
        setError(text[language].failedToArchive);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };

  // Function to handle restoring archived employee
  const handleRestoreEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmRestore)) {
      try {
        // Set loading state
        setLoading(true);

        // Find the archived employee
        const employeeToRestore = archivedEmployees.find(
          (emp) => emp.id === employeeId
        );

        // Remove archived properties and set status to active
        const restoredEmployee = {
          ...employeeToRestore,
          status: "Active",
        };
        // Remove archived date property
        delete restoredEmployee.archivedDate;

        // Restore to active employees
        await apiFetch(`${API_URL}/employees/restore/${employeeId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          employeeId: employeeId,
          employeeData: restoredEmployee,
        });

        // Remove from archived
        await apiFetch(`${API_URL}/employees/archived/${employeeId}`, { method: "DELETE" });

        // Update local states
        setArchivedEmployees(
          archivedEmployees.filter((emp) => emp.id !== employeeId)
        );
        setEmployees([...employees, restoredEmployee]);
        setArchivedCount(archivedCount - 1);
        setSuccess(text[language].employeeRestored);

        // Refresh counts
        fetchEmployeeCount();
        fetchEmployeeCounts();
      } catch (err) {
        // Log error and show error message
        console.error("Failed to restore employee:", err);
        setError(text[language].failedToRestore);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };



  // Function to handle Excel export
  const handleExportToExcel = async () => {
    try {
      // Set loading state
      setLoading(true);

      const pickExportValue = (...values) => {
        for (const value of values) {
          if (value === null || value === undefined) continue;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed || trimmed === "N/A" || trimmed === "NA") continue;
            return value;
          }
          return value;
        }
        return "N/A";
      };

      // Prepare export data with all employee details
      const exportData = filteredEmployees.map((employee) => ({
        "Employee ID": employee.id,
        "Full Name": employee.name,
        Email: employee.email,
        Phone: employee.phone,
        Position: employee.position,
        Department: employee.employeeType,
        Company: employee.company,
        Status: employee.status,
        Salary: employee.salary,
        "Join Date": employee.joinDate
          ? new Date(employee.joinDate).toLocaleDateString()
          : "N/A",
        "Arabic Name": pickExportValue(
          employee.fullEmployeeData?.arabicName,
          employee.arabicName,
          employee.fullEmployeeData?.nameArabic,
          employee.nameArabic
        ),
        "National ID": employee.fullEmployeeData?.nationalId || "N/A",
        "Date of Birth": employee.fullEmployeeData?.dateOfBirth
          ? new Date(employee.fullEmployeeData.dateOfBirth).toLocaleDateString()
          : "N/A",
        Age: employee.fullEmployeeData?.age || "N/A",
        Gender: employee.fullEmployeeData?.gender || "N/A",
        "Emergency Phone": employee.fullEmployeeData?.emergencyPhone || "N/A",
        Address: pickExportValue(
          employee.fullEmployeeData?.address,
          employee.fullEmployeeData?.arabicAddress,
          employee.address,
          employee.arabicAddress
        ),
        City: employee.fullEmployeeData?.city || "N/A",
        Country: employee.fullEmployeeData?.country || "N/A",
        Nationality: employee.fullEmployeeData?.nationality || "N/A",
        "Marital Status": employee.fullEmployeeData?.maritalStatus || "N/A",
        Religion: employee.fullEmployeeData?.religion || "N/A",
        "Client Name": employee.fullEmployeeData?.clientName || "N/A",
        "Client Code": employee.fullEmployeeData?.clientCode || "N/A",
        "Working Age Years": employee.fullEmployeeData?.workingAgeYears || 0,
        "Working Age Months": employee.fullEmployeeData?.workingAgeMonths || 0,
        "Labor Mode": employee.fullEmployeeData?.laborMode || "N/A",
        "Contract Date": employee.fullEmployeeData?.contractDate
          ? new Date(
            employee.fullEmployeeData.contractDate
          ).toLocaleDateString()
          : "N/A",
        "Contract Expiration": employee.fullEmployeeData?.contractExpiration
          ? new Date(
            employee.fullEmployeeData.contractExpiration
          ).toLocaleDateString()
          : "N/A",
        "Term of Contract": employee.fullEmployeeData?.termOfContract || "N/A",
        "Insurance Status": employee.fullEmployeeData?.insuranceStatus || "N/A",
        "Insurance Number": employee.fullEmployeeData?.insuranceNumber || "N/A",
        "Insurance Salary": employee.fullEmployeeData?.insuranceSalary || 0,
        "Insurance Start Date": employee.fullEmployeeData?.insuredBeginningDate
          ? new Date(
            employee.fullEmployeeData.insuredBeginningDate
          ).toLocaleDateString()
          : "N/A",
        "Months for Insurance":
          employee.fullEmployeeData?.monthsForInsurance || 0,
        "Graduate Institution":
          employee.fullEmployeeData?.graduateInstitution || "N/A",
        Major: employee.fullEmployeeData?.major || "N/A",
        "Graduation Year": employee.fullEmployeeData?.graduationYear || "N/A",
        "Created At": employee.fullEmployeeData?.createdAt
          ? new Date(employee.fullEmployeeData.createdAt).toLocaleDateString()
          : "N/A",
        "Updated At": employee.fullEmployeeData?.updatedAt
          ? new Date(employee.fullEmployeeData.updatedAt).toLocaleDateString()
          : "N/A",
      }));

      // Make API call to export filtered data
      const response = await apiFetch(
        `${API_URL}/employees/export-filtered`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: exportData,
            filters: {
              searchTerm,
              filterDepartment,
              filterStatus,
              filterCompany,
              totalRecords: filteredEmployees.length,
            },
          }),
        }
      );

      // Create blob URL for download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Create download link element
      const link = document.createElement("a");
      link.href = url;

      // Generate filename based on filters
      let filename = "employees_filtered";
      if (searchTerm) filename += `_search-${searchTerm}`;
      if (filterDepartment) filename += `_dept-${filterDepartment}`;
      if (filterStatus) filename += `_status-${filterStatus}`;
      if (filterCompany) filename += `_company-${filterCompany}`;
      filename += `_${new Date().getTime()}.xlsx`;

      // Set download filename
      link.setAttribute("download", filename);
      // Add link to DOM and trigger click
      document.body.appendChild(link);
      link.click();
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      setSuccess(
        `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].filteredEmployees}`
      );
    } catch (err) {
      // Log error
      console.error("Export failed:", err);

      // Fallback to CSV export if Excel export fails
      if (err.response?.status === 404 || err.code === "ERR_NETWORK") {
        try {
          // Generate CSV content
          const csvContent = generateCSV(filteredEmployees);
          // Download CSV file
          downloadCSV(
            csvContent,
            `employees_filtered_${new Date().getTime()}.csv`
          );
          setSuccess(
            `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].exportSuccessCSV}`
          );
        } catch (csvError) {
          setError(text[language].failedToExport);
        }
      } else {
        setError(text[language].failedToExport);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to generate CSV content from employees data
  const generateCSV = (employees) => {
    // Return empty string if no employees
    if (employees.length === 0) return "";

    // Define CSV headers
    const headers = [
      "Employee ID",
      "Full Name",
      "Email",
      "Phone",
      "Labor Mode",
      "Department",
      "Company",
      "Status",
      "Salary",
      "Join Date",
    ];

    // Create CSV rows array starting with headers
    const csvRows = [headers.join(",")];

    // Add employee data rows
    employees.forEach((emp) => {
      const row = [
        emp.id,
        `"${emp.name}"`, // Wrap in quotes to handle commas in names
        emp.email,
        emp.phone,
        `"${emp.laborMode}"`,
        `"${emp.department}"`,
        `"${emp.company}"`,
        emp.status,
        emp.salary || 0,
        emp.joinDate ? new Date(emp.joinDate).toLocaleDateString() : "N/A",
      ];
      csvRows.push(row.join(","));
    });

    // Return CSV content as string
    return csvRows.join("\n");
  };

  // Function to download CSV file
  const downloadCSV = (csvContent, filename) => {
    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter employees based on search and filter criteria
  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      (employee.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (employee.id?.toString() || "").includes(searchTerm.toLowerCase()) ||
      (employee.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    const matchesDepartment =
      filterDepartment === "" ||
      (employee.department?.toLowerCase() || "").includes(filterDepartment.toLowerCase());

    const matchesStatus =
      filterStatus === "" || employee.status === filterStatus;

    // ✅ FIXED: Use workingCompany from fullEmployeeData
    const matchesCompany =
      filterCompany === "" ||
      employee.fullEmployeeData?.workingCompany === filterCompany;

    return (
      matchesSearch && matchesDepartment && matchesStatus && matchesCompany
    );
  });

  // Function to get status badge styling and text
  const getStatusBadge = (status) => {
    // Base styles for all status badges
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      fontSize: fonts.small,
      fontWeight: "600",
    };

    // Return styled span based on status
    switch (status) {
      case "Active":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#d1fae5",
              color: "#065f46",
            }}
          >
            {text[language].statusActive}
          </span>
        );
      case "OnLeave":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fef3c7",
              color: "#92400e",
            }}
          >
            {text[language].statusOnLeave}
          </span>
        );
      case "Inactive":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {text[language].statusInactive}
          </span>
        );
      case "Archived":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#6b7280",
            }}
          >
            {text[language].statusArchived}
          </span>
        );
      default:
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#374151",
            }}
          >
            {text[language].statusUnknown}
          </span>
        );
    }
  };

  // Function to get dynamic responsive styles based on theme and screen size
  const getResponsiveStyles = () => ({
    // ===== RESPONSIVE HEADER CONTAINER =====
    header: {
      background:
        theme === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(255, 255, 255, 0.08)",
      border:
        theme === "light"
          ? "1px solid rgba(0, 0, 0, 0.1)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        screenSize === "xs"
          ? "1rem"
          : screenSize === "sm"
            ? "1.25rem"
            : "1.5rem",
      padding: spacing.padding,
      marginBottom: spacing.margin,
      backdropFilter: "blur(0px)",
      boxShadow:
        theme === "light"
          ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)"
          : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE SECONDARY HEADER =====
    header2: {
      background:
        theme === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(255, 255, 255, 0.08)",
      border:
        theme === "light"
          ? "1px solid rgba(0, 0, 0, 0.1)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        screenSize === "xs"
          ? "1rem"
          : screenSize === "sm"
            ? "1.25rem"
            : "1.5rem",
      padding: spacing.padding,
      height: screenSize === "xs" ? "auto" : "3.125rem",
      marginBottom: spacing.margin,
      backdropFilter: "blur(0px)",
      boxShadow:
        theme === "light"
          ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)"
          : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE MAIN CONTAINER =====
    container: {
      // minHeight: "100%",
      // fontFamily:
      //   "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      // padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1rem",
      // position: "relative",
      // maxWidth: container.maxWidth,
      // margin: "0 auto",
      direction: language === "ar" ? "rtl" : "ltr", // ✅ أضف هذا السطر
    },

    // ===== RESPONSIVE ALERT NOTIFICATION =====
    alert: {
      marginTop: "5%",
      position: "fixed",
      top: screenSize === "xs" ? "0.5rem" : "1.25rem",
      right: screenSize === "xs" ? "0.5rem" : "1.25rem",
      zIndex: 3000,
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      border: "1px solid",
      maxWidth: screenSize === "xs" ? "calc(100vw - 1rem)" : "25rem",
      backdropFilter: "blur(0.625rem)",
      animation: "slideIn 0.3s ease-out",
    },

    // ===== ALERT CLOSE BUTTON =====
    alertClose: {
      background: "none",
      border: "none",
      color: "inherit",
      cursor: "pointer",
      padding: "0.25rem",
      borderRadius: "0.25rem",
      marginLeft: "auto",
    },

    // ===== RESPONSIVE WELCOME SECTION =====
    welcomeSection: {
      marginBottom: screenSize === "xs" ? "1rem" : "1rem",
    },

    // ===== RESPONSIVE WELCOME TITLE =====
    welcomeTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
      textShadow:
        theme === "dark"
          ? "0 0.125rem 0.25rem rgba(0,0,0,0.3)"
          : "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
      lineHeight: "1.2",
      marginBottom: 25,
    },
    selectOption: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.9)",
      color: theme === "dark" ? "#f8fafc" : "#0f172a",
      padding: "clamp(10px, 1.5vw, 14px) 18px",
      fontWeight: 600,
      fontSize: "clamp(14px, 1.2vw, 16px)",
      borderRadius: "clamp(10px, 1.5vw, 14px)",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
      boxShadow: theme === "dark"
        ? "0 4px 15px rgba(0,0,0,0.5)"
        : "0 4px 15px rgba(0,0,0,0.1)",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.25s ease-in-out",
      marginBottom: "30px",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    },

    // ===== RESPONSIVE WELCOME SUBTITLE =====
    welcomeSubtitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.subtitle,
      margin: 0,
      textShadow:
        theme === "dark"
          ? "0 0.0625rem 0.125rem rgba(0,0,0,0.3)"
          : "0 0.0625rem 0.125rem rgba(0,0,0,0.1)",
      lineHeight: "1.4",
    },

    // ===== RESPONSIVE TOP BUTTONS CONTAINER =====
    topButtonsContainer: {
      display: "flex",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: screenSize === "xs" ? "center" : "flex-start",
    },

    // ===== RESPONSIVE PRIMARY BUTTON =====
    primaryBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
      borderRadius: screenSize === "xs" ? "0.625rem" : "0.625rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },

    // ===== RESPONSIVE SECONDARY BUTTON =====
    secondaryBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
      borderRadius: screenSize === "xs" ? "0.625rem" : "0.625rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },



    // ===== RESPONSIVE STATISTICS CARD =====
    statCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      padding: "1.55rem",
      marginTop: -40,
      marginLeft: 5,
      borderRadius:
        screenSize === "xs" ? "1rem" : screenSize === "sm" ? "1.25rem" : "1rem",
      transition: "all 0.3s ease",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      marginBottom: 70,
      cursor: "pointer",
    },

    // ===== RESPONSIVE GENERAL CARD =====
    card: {
      background:
        theme === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: spacing.padding,
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
          : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
      marginBottom: spacing.margin,
      transition: "all 0.3s ease",
      cursor: "pointer",
    },

    // ===== RESPONSIVE CARD TITLE =====
    cardTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `0 0 ${spacing.margin} 0`,
      textShadow:
        theme === "dark"
          ? "0 0.0625rem 0.125rem rgba(0,0,0,0.3)"
          : "0 0.0625rem 0.125rem rgba(0,0,0,0.1)",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
    },

    // ===== RESPONSIVE FILTER CONTAINER =====
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : screenSize === "md"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
    },

    // ===== FILTER GROUP =====
    filterGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "0.375rem",
    },

    // ===== RESPONSIVE FILTER LABEL =====
    filterLabel: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.small,
      fontWeight: "500",
      marginBottom: "0.25rem",
    },

    // ===== RESPONSIVE FILTER INPUT =====
    filterInput: {
      // Use unified control style so inputs and dropdowns match visually
      ...unifiedControlStyle(theme, language === "ar"),
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      padding: screenSize === "xs" ? "0.625rem 0.75rem" : "0.625rem 0.75rem",
      fontSize: fonts.body,
      transition: "border-color 0.2s",
      width: "100%",
    },

    // ===== RESPONSIVE TABLE CONTAINER =====
    tableContainer: {
      overflowX: "auto",
      marginTop: spacing.margin,
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      position: "relative",
      overflow: "hidden",
      // Add horizontal scroll for mobile
      ...(screenSize === "xs" && {
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }),
    },

    // ===== RESPONSIVE TABLE WRAPPER =====
    tableWrapper: {
      position: "relative",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      overflow: "hidden",
    },

    // ===== RESPONSIVE TABLE =====
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth:
        screenSize === "xs" ? "600px" : screenSize === "sm" ? "700px" : "900px",
      position: "relative",
      zIndex: 1,
    },

    // ===== RESPONSIVE TABLE HEADER =====
    tableHeader: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      textAlign: "left",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.1)",
      whiteSpace: screenSize === "xs" ? "nowrap" : "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backdropFilter: "blur(0.625rem)",
      boxShadow:
        theme === "dark"
          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE TABLE CELL =====
    tableCell: {
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      verticalAlign: "middle",
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
    },

    // ===== RESPONSIVE TABLE ROW =====
    tableRow: {
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      position: "relative",
    },

    // ===== RESPONSIVE USER AVATAR =====
    userAvatar: {
      width:
        screenSize === "xs"
          ? "2rem"
          : screenSize === "sm"
            ? "2.25rem"
            : "2.5rem",
      height:
        screenSize === "xs"
          ? "2rem"
          : screenSize === "sm"
            ? "2.25rem"
            : "2.5rem",
      borderRadius: "150%",
      background: "linear-gradient(135deg, #2e7cba 0%, #22354d 100%)",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize:
        screenSize === "xs"
          ? "0.75rem"
          : screenSize === "sm"
            ? "0.875rem"
            : "1rem",
      fontWeight: "700",
      position: "relative",
      border: "0.7px solid rgba(255, 255, 255, 0.2)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 1rem rgba(0, 0, 0, 0.4)"
          : "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
      transition: "all 0.3s ease",
      flexShrink: 0,
    },

    // ===== RESPONSIVE ACTION BUTTON =====
    actionBtn: {
      background: "rgba(59, 130, 246, 0.1)",
      color: "#2e7cba",
      border: "1px solid rgba(59, 130, 246, 0.2)",
      padding: screenSize === "xs" ? "0.375rem 0.5rem" : "0.5rem 0.75rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      cursor: "pointer",
      fontSize: fonts.small,
      fontWeight: "500",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: screenSize === "xs" ? "2rem" : "2.25rem",
      height: screenSize === "xs" ? "2rem" : "2.25rem",
    },

    // ===== SPECIFIC ACTION BUTTON STYLES =====
    viewBtn: {
      background: "rgba(16, 185, 129, 0.1)",
      color: "#10b981",
      border: "1px solid rgba(16, 185, 129, 0.2)",
    },

    editBtn: {
      background: "rgba(245, 158, 11, 0.1)",
      color: "#f59e0b",
      border: "1px solid rgba(245, 158, 11, 0.2)",
    },

    deleteBtn: {
      background: "rgba(239, 68, 68, 0.1)",
      color: "#ef4444",
      border: "1px solid rgba(239, 68, 68, 0.2)",
    },

    archiveBtn: {
      background: "rgba(107, 114, 128, 0.1)",
      color: "#6b7280",
      border: "1px solid rgba(107, 114, 128, 0.2)",
    },

    SendBtn: {
      background: "rgba(107, 114, 128, 0.1)",
      color: "#0055ffff",
      border: "1px solid rgba(107, 114, 128, 0.2)",
    },

    restoreBtn: {
      background: "rgba(34, 197, 94, 0.1)",
      color: "#22c55e",
      border: "1px solid rgba(34, 197, 94, 0.2)",
    },

    // ===== RESPONSIVE TOGGLE BUTTON =====
    toggleBtn: {
      background:
        theme === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.1)",
      border:
        theme === "dark"
          ? "1px solid rgba(59, 130, 246, 0.4)"
          : "1px solid rgba(59, 130, 246, 0.3)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      padding: screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
      cursor: "pointer",
      color: theme === "dark" ? "#d1d5db" : "#374151",
      fontWeight: "500",
      fontSize: fonts.body,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.375rem",
      transition: "all 0.2s ease",
    },

    // ===== RESPONSIVE MODAL =====
    modal: {
      position: "fixed",
      top: screenSize === "xs" ? 0 : -80,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: screenSize === "xs" ? "flex-start" : "flex-start",
      justifyContent: "center",
      zIndex: 2000,
      backdropFilter: "blur(0.3125rem)",
      padding: screenSize === "xs" ? "0" : "0",
      overflowY: "scroll", // مهم
      scrollbarWidth: "none", // Firefox
      backdropFilter: "blur(10px)",

      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    // ===== RESPONSIVE MODAL CONTENT =====
    modalContent: {
      background:
        theme === "dark"
          ? "rgba(15, 23, 42, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(1.5625rem)",
      backdropFilter: "blur(10px)",

      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth:
        screenSize === "xs"
          ? "100%"
          : screenSize === "sm"
            ? "35rem"
            : "43.75rem",
      margin: screenSize === "xs" ? "0" : "11.875rem auto",
      maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      overflowY: "scroll", // مهم
      scrollbarWidth: "none", // Firefox
      backdropFilter: "blur(10px)",

      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },

    modalContent1: {
      background:
        theme === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth:
        screenSize === "xs"
          ? "100%"
          : screenSize === "sm"
            ? "50rem"
            : "93.75rem",
      margin: screenSize === "xs" ? "0" : "11.875rem auto",
      maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      overflowY: "auto",
      backdropFilter: "blur(10px)",

      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },

    // ===== RESPONSIVE VIEW MODAL CONTENT =====
    viewModalContent: {
      background:
        theme === "dark"
          ? "rgba(15, 23, 42, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth: screenSize === "xs" ? "100%" : "50%",
      maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      marginTop: screenSize === "xs" ? "0" : "10%",
      marginBottom: screenSize === "xs" ? "0" : "5%",
      overflowY: "scroll", // مهم
      scrollbarWidth: "none", // Firefox
      backdropFilter: "blur(10px)",

      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },

    // ===== VIEW MODAL BODY =====
    viewModalBody: {
      display: "flex",
      flexDirection: "column",
      gap: spacing.margin,
    },

    // ===== RESPONSIVE EMPLOYEE HEADER =====
    employeeHeader: {
      display: "flex",
      alignItems: "center",
      gap: spacing.margin,
      padding: spacing.padding,
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      flexDirection: screenSize === "xs" ? "column" : "row",
      textAlign: screenSize === "xs" ? "center" : "left",
    },

    // ===== RESPONSIVE LARGE AVATAR =====
    largeAvatar: {
      width: screenSize === "xs" ? "4rem" : "5rem",
      height: screenSize === "xs" ? "4rem" : "5rem",
      borderRadius: "50%",
      fontSize: screenSize === "xs" ? "1.5rem" : "1.75rem",
      background: "#2e7cba",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "700",
      boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.3)",
      flexShrink: 0,
    },

    // ===== EMPLOYEE HEADER INFO =====
    employeeHeaderInfo: {
      flex: 1,
    },

    // ===== RESPONSIVE EMPLOYEE NAME =====
    employeeName: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: screenSize === "xs" ? "1.5rem" : "1.75rem",
      fontWeight: "700",
      margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
    },

    // ===== RESPONSIVE EMPLOYEE TITLE =====
    employeeTitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.subtitle,
      fontWeight: "500",
      margin: "0 0 0.25rem 0",
    },

    // ===== RESPONSIVE EMPLOYEE DEPARTMENT =====
    employeeDepartment: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
      fontSize: fonts.body,
      margin: "0 0 0.75rem 0",
    },

    // ===== EMPLOYEE STATUS =====
    employeeStatus: {
      display: "inline-block",
    },

    // ===== RESPONSIVE INFORMATION SECTION =====
    infoSection: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      padding: spacing.padding,
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
    },

    // ===== RESPONSIVE SECTION HEADER =====
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      marginBottom: spacing.margin,
      paddingBottom: screenSize === "xs" ? "0.5rem" : "0.75rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },

    // ===== RESPONSIVE INFORMATION GRID =====
    infoGrid: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "1fr"
            : "repeat(auto-fit, minmax(18.75rem, 1fr))",
      gap: spacing.margin,
    },

    // ===== RESPONSIVE INFO ITEM =====
    infoItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      padding: screenSize === "xs" ? "0.75rem" : "1rem",
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.05)"
          : "1px solid rgba(0, 0, 0, 0.03)",
    },

    // ===== INFO LABEL =====
    infoLabel: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
      fontSize: fonts.tiny,
      fontWeight: "500",
      textTransform: "uppercase",
      letterSpacing: "0.03125rem",
      marginBottom: "0.25rem",
    },

    // ===== INFO VALUE =====
    infoValue: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      fontWeight: "500",
      lineHeight: "1.4",
    },

    // ===== RESPONSIVE MODAL HEADER =====
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: screenSize === "xs" ? "1.5rem" : "2rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      paddingBottom: screenSize === "xs" ? "0.75rem" : "1rem",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
      gap: screenSize === "xs" ? "1rem" : "0",
      overflowY: "scroll", // مهم
      scrollbarWidth: "none", // Firefox


    },

    // ===== RESPONSIVE MODAL TITLE =====
    modalTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
      fontWeight: "700",
      margin: 0,
    },

    // ===== CLOSE BUTTON =====
    closeBtn: {
      background: "none",
      border: "none",
      color: theme === "dark" ? "white" : "#1a1a1a",
      cursor: "pointer",
      padding: "0.5rem",
      borderRadius: "0.5rem",
      transition: "background 0.2s",
    },

    // ===== RESPONSIVE SECTION TITLE =====
    sectionTitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(26, 26, 26, 0.8)",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `${spacing.margin} 0 ${screenSize === "xs" ? "0.75rem" : "1rem"
        } 0`,
      paddingBottom: "0.5rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },

    // ===== FORM GROUP =====
    formGroup: {
      marginBottom: spacing.margin,
    },

    // ===== RESPONSIVE FORM LABEL =====
    formLabel: {
      display: "block",
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.body,
      fontWeight: "500",
      marginBottom: "0.5rem",
    },

    // ===== RESPONSIVE FORM INPUT =====
    formInput: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      padding: screenSize === "xs" ? "0.75rem" : "0.75rem",
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      outline: "none",
      width: "100%",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    formSelectOption: {
      background: theme === "dark" ? "#333" : "#fff",
      color: theme === "dark" ? "#fff" : "#000",
    },

    // ===== RESPONSIVE FORM SELECT =====
    formSelect: {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",

      background:
        theme === "dark"
          ? "  rgba(255, 255, 255, 0.1)"
          : "rgba(45, 39, 39, 0.2)",

      color: theme === "dark" ? "#f1f5f9" : "#1e293b",

      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.15)",

      borderRadius: "0.625rem",
      padding: "0.7rem ",
      fontSize: fonts.body,
      fontWeight: "500",
      width: "100%",
      //boxSizing: "border-box",
      outline: "none",
      cursor: "pointer",

      // backgroundImage: `url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='${theme === "dark" ? "%23f1f5f9" : "%231e293b"}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      // backgroundPosition: "right 0.875rem center",

      // boxShadow: theme === "dark"
      //   ? "0 2px 8px rgba(0,0,0,0.3)"
      //   : "0 2px 8px rgba(0,0,0,0.08)",

      // transition: "all 0.2s ease",
    },

    // ===== RESPONSIVE FORM ROW =====
    formRow: {
      display: "grid",
      gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
      gap: spacing.gap,
    },

    // ===== RESPONSIVE MODAL ACTIONS =====
    modalActions: {
      display: "flex",
      gap: screenSize === "xs" ? "0.75rem" : "0.75rem",
      justifyContent: screenSize === "xs" ? "center" : "flex-end",
      marginTop: screenSize === "xs" ? "1.5rem" : "2rem",
      paddingTop: spacing.padding,
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
    },

    // ===== ERROR TEXT =====
    errorText: {
      color: "#ef4444",
      fontSize: fonts.small,
      marginTop: "0.25rem",
      display: "block",
    },
  });

  // Get styles object
  const styles = getResponsiveStyles();

  // ===== RESPONSIVE HOVER EFFECTS =====
  const getPrimaryBtnHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-1px)" : "translateY(-2px)",
    boxShadow:
      screenSize === "xs"
        ? "0 0.25rem 0.75rem rgba(59, 130, 246, 0.3)"
        : "0 0.375rem 1.25rem rgba(59, 130, 246, 0.4)",
  });

  const getPrimaryBtnDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow: "none",
  });

  const getSecondaryBtnHoverStyles = (hoverColor = "#10b981") => ({
    background: `rgba(${hoverColor === "#10b981"
      ? "16, 185, 129"
      : hoverColor === "#f59e0b"
        ? "245, 158, 11"
        : "255, 255, 255"
      }, 0.2)`,
    borderColor: hoverColor,
    color: hoverColor,
  });

  const getSecondaryBtnDefaultStyles = () => ({
    background:
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
    borderColor:
      theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)",
    color: theme === "dark" ? "white" : "#1a1a1a",
  });

  const getCloseBtnHoverStyles = () => ({
    background:
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
  });

  const getCloseBtnDefaultStyles = () => ({
    background: "none",
  });

  const getCardHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-1px)" : "translateY(-2px)",
    boxShadow:
      theme === "dark"
        ? screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0, 0, 0, 0.3)"
          : "0 0.75rem 2.5rem rgba(0, 0, 0, 0.3)"
        : screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0, 0, 0, 0.15)"
          : "0 0.75rem 2.5rem rgba(0, 0, 0, 0.15)",
  });

  const getCardDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow:
      theme === "dark"
        ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
        : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
  });

  // ===== EVENT HANDLERS FOR HOVER EFFECTS =====
  const handlePrimaryBtnMouseEnter = (e) => {
    if (!loading) {
      const hoverStyles = getPrimaryBtnHoverStyles();
      Object.assign(e.currentTarget.style, hoverStyles);
    }
  };

  const handlePrimaryBtnMouseLeave = (e) => {
    if (!loading) {
      const defaultStyles = getPrimaryBtnDefaultStyles();
      Object.assign(e.currentTarget.style, defaultStyles);
    }
  };

  const handleSecondaryBtnMouseEnter = (e, hoverColor = "#10b981") => {
    if (!loading) {
      const hoverStyles = getSecondaryBtnHoverStyles(hoverColor);
      Object.assign(e.currentTarget.style, hoverStyles);
    }
  };

  const handleSecondaryBtnMouseLeave = (e) => {
    if (!loading) {
      const defaultStyles = getSecondaryBtnDefaultStyles();
      Object.assign(e.currentTarget.style, defaultStyles);
    }
  };

  const handleCloseBtnMouseEnter = (e) => {
    const hoverStyles = getCloseBtnHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleCloseBtnMouseLeave = (e) => {
    const defaultStyles = getCloseBtnDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  const handleCardMouseEnter = (e) => {
    const hoverStyles = getCardHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleCardMouseLeave = (e) => {
    const defaultStyles = getCardDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  // Action button hover effects
  const handleActionBtnMouseEnter = (e, type) => {
    const hoverStyles = {
      transform:
        screenSize === "xs" ? "translateY(-0.5px)" : "translateY(-1px)",
      boxShadow:
        screenSize === "xs"
          ? "0 0.25rem 0.5rem rgba(0,0,0,0.1)"
          : "0 0.25rem 0.75rem rgba(0,0,0,0.15)",
    };

    if (type === "view") {
      hoverStyles.background = "rgba(16, 185, 129, 0.2)";
    } else if (type === "edit") {
      hoverStyles.background = "rgba(245, 158, 11, 0.2)";
    } else if (type === "delete") {
      hoverStyles.background = "rgba(239, 68, 68, 0.2)";
    } else if (type === "archive") {
      hoverStyles.background = "rgba(107, 114, 128, 0.2)";
    } else if (type === "restore") {
      hoverStyles.background = "rgba(34, 197, 94, 0.2)";
    }

    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleActionBtnMouseLeave = (e, type) => {
    const defaultStyles = {
      transform: "translateY(0)",
      boxShadow: "none",
    };

    if (type === "view") {
      defaultStyles.background = "rgba(16, 185, 129, 0.1)";
    } else if (type === "edit") {
      defaultStyles.background = "rgba(245, 158, 11, 0.1)";
    } else if (type === "delete") {
      defaultStyles.background = "rgba(239, 68, 68, 0.1)";
    } else if (type === "archive") {
      defaultStyles.background = "rgba(107, 114, 128, 0.1)";
    } else if (type === "restore") {
      defaultStyles.background = "rgba(34, 197, 94, 0.1)";
    }

    Object.assign(e.currentTarget.style, defaultStyles);
  };


  // ===== DOCUMENTS MODAL COMPONENT =====
  const DocumentsModal = () => {
    if (!selectedEmployeeDocuments) {
      console.log('⚠️ No employee selected for documents modal');
      return null;
    }

    console.log('📂 Rendering DocumentsModal for:', selectedEmployeeDocuments.name);
    console.log('📋 Current checklist state:', documentChecklist);

    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowDocumentsModal(false);
        setSelectedEmployeeDocuments(null);
      }
    };

    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              <FileText size={screenSize === 'xs' ? 18 : 20} />
              {text[language].documentChecklist} - {
                selectedEmployeeDocuments.name
                  ?.split(' ')
                  .slice(0, 4)
                  .join(' ')
              }

              {hasUnsavedChanges && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginLeft: '1rem',
                  fontSize: fonts.small,
                  color: '#f59e0b',
                  fontWeight: '500'
                }}>
                  ● {language === 'ar' ? 'تغييرات غير محفوظة' : 'Unsaved Changes'}
                </span>
              )}
            </h2>
            <button
              style={styles.closeBtn}
              onClick={() => {
                setShowDocumentsModal(false);
                setSelectedEmployeeDocuments(null);
                setHasUnsavedChanges(false);
              }}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button>
          </div>

          {/* Document Checklist */}
          <div style={styles.viewModalBody}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
              gap: spacing.margin,
              padding: spacing.padding
            }}>
              {/* Photos Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.photosCount}
                  onChange={(e) => handleDocumentCheckboxChange('photosCount', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].photos}
                </span>
                {documentChecklist.photosCount && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* Birth Certificate Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.birthCertificateCopy}
                  onChange={(e) => handleDocumentCheckboxChange('birthCertificateCopy', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].birthForm}
                </span>
                {documentChecklist.birthCertificateCopy && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* Graduation Certificate Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.gradCertificateOriginal}
                  onChange={(e) => handleDocumentCheckboxChange('gradCertificateOriginal', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].graduation}
                </span>
                {documentChecklist.gradCertificateOriginal && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* ID Copy Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.idCopy}
                  onChange={(e) => handleDocumentCheckboxChange('idCopy', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].idCopy}
                </span>
                {documentChecklist.idCopy && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* Criminal Record Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.noCrimeCertificate}
                  onChange={(e) => handleDocumentCheckboxChange('noCrimeCertificate', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].criminalRecord}
                </span>
                {documentChecklist.noCrimeCertificate && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* Work Certificate Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.laborLetterOriginal}
                  onChange={(e) => handleDocumentCheckboxChange('laborLetterOriginal', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].workCertificate}
                </span>
                {documentChecklist.laborLetterOriginal && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>

              {/* Form 111 Checkbox */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={documentChecklist.medicalCheckForm111}
                  onChange={(e) => handleDocumentCheckboxChange('medicalCheckForm111', e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#10b981'
                  }}
                />
                <span style={{
                  fontSize: fonts.body,
                  color: isDarkMode ? '#fff' : '#1f2937',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {text[language].form111}
                </span>
                {documentChecklist.medicalCheckForm111 && (
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                )}
              </label>
            </div>

            {/* Save Button */}
            <div style={{
              padding: spacing.padding,
              paddingTop: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.margin
            }}>
              <button
                onClick={handleSaveDocuments}
                disabled={!hasUnsavedChanges || savingDocument}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                  fontSize: fonts.body,
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  opacity: savingDocument ? 0.7 : 1,
                  ...(hasUnsavedChanges && !savingDocument && {
                    ':hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }
                  })
                }}
                onMouseEnter={(e) => {
                  if (hasUnsavedChanges && !savingDocument) {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasUnsavedChanges && !savingDocument) {
                    e.target.style.backgroundColor = '#10b981';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {savingDocument ? (
                  <>
                    <Save size={18} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                    {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
  const ViewEmployeeModal = () => {
    // Return null if no employee is selected
    if (!selectedEmployee) return null;

    // ✅ FIX: Safely access fullEmployeeData with fallback
    const employeeData = selectedEmployee.fullEmployeeData || {};

    // Function to format date strings
    const formatDate = (dateString) => {
      if (!dateString) return text[language].na;
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: screenSize === "xs" ? "short" : "long",
        day: "numeric",
      });
    };

    // Function to format currency amounts
    const formatCurrency = (amount) => {
      if (!amount || amount === 0) return text[language].na;
      return `$${amount.toLocaleString()}`;
    };

    // Return modal JSX
    return (
      <div
        style={styles.modal}
        onClick={() => setShowViewEmployee(false)}
      >
        <div
          style={styles.viewModalContent}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].employeeDetails}</h2>
            <button
              style={styles.closeBtn}
              onClick={() => setShowViewEmployee(false)}
            // onMouseEnter={handleCloseBtnMouseEnter}
            // onMouseLeave={handleCloseBtnMouseLeave}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button>
          </div>

          {/* Modal Body */}
          <div style={styles.viewModalBody}>
            {/* Employee Header Section */}
            <div style={{
              ...styles.employeeHeader,
              flexDirection: 'row',
              alignItems: 'center',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.margin,
                flex: 1
              }}>
                {/* Profile Picture */}
                {employeeProfilePics[selectedEmployee.id] ? (
                  <img
                    src={employeeProfilePics[selectedEmployee.id]}
                    alt={selectedEmployee.name}
                    style={{
                      ...styles.largeAvatar,
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}

                <div
                  style={{
                    ...styles.largeAvatar,
                    display: employeeProfilePics[selectedEmployee.id] ? 'none' : 'flex'
                  }}
                >
                  {getUserInitials(selectedEmployee?.name)}
                </div>

                {/* Employee Info */}
                <div style={{
                  ...styles.employeeHeaderInfo,
                  textAlign: language === 'ar' ? 'right' : 'left'
                }}>
                  <h3 style={styles.employeeName}>{selectedEmployee?.name}</h3>
                  <p style={styles.employeeTitle}>{selectedEmployee?.position}</p>
                  <p style={styles.employeeDepartment}>
                    {selectedEmployee?.department}
                  </p>
                  <div style={styles.employeeStatus}>
                    {getStatusBadge(selectedEmployee?.status)}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div style={styles.infoSection}>
              <h4 style={styles.sectionHeader}>
                <Mail size={screenSize === "xs" ? 16 : 18} />
                {text[language].contactInformation}
              </h4>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Mail size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].email}</p>
                    <p style={styles.infoValue}>{selectedEmployee?.email || text[language].na}</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Phone size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].phone}</p>
                    <p style={styles.infoValue}>{selectedEmployee?.phone || text[language].na}</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].nationalId}</p>
                    <p style={styles.infoValue}>
                      {employeeData?.nationalId || text[language].na}
                    </p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Phone size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>
                      {text[language].emergencyPhone}
                    </p>
                    <p style={styles.infoValue}>
                      {employeeData?.emergencyPhone || text[language].na}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Building size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].workInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Building size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].company}</div>
                    <div style={styles.infoValue}>
                      {selectedEmployee.company || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].hiringDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(selectedEmployee.joinDate)}
                    </div>
                  </div>
                </div>

                <div style={styles.infoItem}>
                  <Clock size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].workingExperience}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.workingAgeYears || 0}{" "}
                      {text[language].years},{" "}
                      {employeeData.workingAgeMonths || 0}{" "}
                      {text[language].months}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Award size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].laborMode}</div>
                    <div style={styles.infoValue}>
                      {employeeData.laborMode || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].contractDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.contractDate)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Home size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].personalInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <MapPin size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].address}</div>
                    <div style={styles.infoValue}>
                      {employeeData.address || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <MapPin size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].city}</div>
                    <div style={styles.infoValue}>
                      {employeeData.city || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Flag size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].country}</div>
                    <div style={styles.infoValue}>
                      {employeeData.country || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Flag size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].nationality}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.nationality || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Heart size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].maritalStatus}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.maritalStatus || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].gender}</div>
                    <div style={styles.infoValue}>
                      {employeeData.gender || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Education Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <GraduationCap size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].educationInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <GraduationCap size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].graduateInstitution}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.graduateInstitution || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Award size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].major}</div>
                    <div style={styles.infoValue}>
                      {employeeData.major || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].graduationYear}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.graduationYear || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Insurance Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Shield size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].insuranceInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Shield size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceStatus}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.insuranceStatus || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Shield size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceNumber}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.insuranceNumber || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <DollarSign size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceSalary}
                    </div>
                    <div style={styles.infoValue}>
                      {formatCurrency(employeeData.insuranceSalary)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceStartDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.insuredBeginningDate)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Clock size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].monthsForInsurance}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.monthsForInsurance || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Clock size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].systemInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].employeeId}
                    </div>
                    <div style={styles.infoValue}>{selectedEmployee.id}</div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].createdAt}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.createdAt)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].lastUpdated}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Checklist Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <FileText size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].documentChecklist}</span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
                gap: spacing.margin,
                padding: spacing.padding
              }}>
                {/* Photos Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.photosCount}
                    onChange={(e) => handleDocumentCheckboxChange('photosCount', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].photos}
                  </span>
                  {documentChecklist.photosCount && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* Birth Certificate Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.birthCertificateCopy}
                    onChange={(e) => handleDocumentCheckboxChange('birthCertificateCopy', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].birthForm}
                  </span>
                  {documentChecklist.birthCertificateCopy && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* Graduation Certificate Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.gradCertificateOriginal}
                    onChange={(e) => handleDocumentCheckboxChange('gradCertificateOriginal', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].graduation}
                  </span>
                  {documentChecklist.gradCertificateOriginal && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* ID Copy Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.idCopy}
                    onChange={(e) => handleDocumentCheckboxChange('idCopy', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].idCopy}
                  </span>
                  {documentChecklist.idCopy && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* Criminal Record Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.noCrimeCertificate}
                    onChange={(e) => handleDocumentCheckboxChange('noCrimeCertificate', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].criminalRecord}
                  </span>
                  {documentChecklist.noCrimeCertificate && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* Work Certificate Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.laborLetterOriginal}
                    onChange={(e) => handleDocumentCheckboxChange('laborLetterOriginal', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].workCertificate}
                  </span>
                  {documentChecklist.laborLetterOriginal && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>

                {/* Form 111 Checkbox */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={documentChecklist.medicalCheckForm111}
                    onChange={(e) => handleDocumentCheckboxChange('medicalCheckForm111', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language].form111}
                  </span>
                  {documentChecklist.medicalCheckForm111 && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>
              </div>

              {/* Save Button for Document Checklist */}
              <div style={{
                padding: spacing.padding,
                paddingTop: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: spacing.margin
              }}>
                <button
                  onClick={handleSaveDocuments}
                  disabled={!hasUnsavedChanges || savingDocument}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                    fontSize: fonts.body,
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    opacity: savingDocument ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (hasUnsavedChanges && !savingDocument) {
                      e.target.style.backgroundColor = '#059669';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hasUnsavedChanges && !savingDocument) {
                      e.target.style.backgroundColor = '#10b981';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {savingDocument ? (
                    <>
                      <Save size={18} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                      {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE EDIT EMPLOYEE MODAL COMPONENT =====
  const EditEmployeeModal = () => {
    // Initialize form data with selected employee data
    const [formData, setFormData] = useState({
      name: selectedEmployee?.name || "",
      email: selectedEmployee?.email || "",
      phone: selectedEmployee?.phone || "",
      department: selectedEmployee?.department || "",
      laborMode: selectedEmployee?.laborMode || "",
      company: selectedEmployee?.company || "",
      status: selectedEmployee?.status || "Active",
      salary: selectedEmployee?.salary || "",
      nationalId: selectedEmployee?.fullEmployeeData?.nationalId || "",
      address: selectedEmployee?.fullEmployeeData?.address || "",
      city: selectedEmployee?.fullEmployeeData?.city || "",
      country: selectedEmployee?.fullEmployeeData?.country || "Egypt",
      nationality: selectedEmployee?.fullEmployeeData?.nationality || "",
      gender: selectedEmployee?.fullEmployeeData?.gender || "",
      maritalStatus: selectedEmployee?.fullEmployeeData?.maritalStatus || "",
      dateOfBirth: selectedEmployee?.fullEmployeeData?.dateOfBirth
        ? new Date(selectedEmployee.fullEmployeeData.dateOfBirth)
          .toISOString()
          .split("T")[0]
        : "",
    });

    // Form validation errors state
    const [formErrors, setFormErrors] = useState({});

    // Handle input changes
    const handleInputChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });

      // Clear error when user starts typing
      if (formErrors[e.target.name]) {
        setFormErrors({
          ...formErrors,
          [e.target.name]: "",
        });
      }
    };

    // Validate form data
    const validateForm = () => {
      const errors = {};

      // Check if name is provided
      if (!formData.name.trim()) {
        errors.name = text[language].nameRequired;
      }

      // Check if email is provided and valid
      if (!formData.email.trim()) {
        errors.email = text[language].emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = text[language].emailInvalid;
      }

      // Check if department is provided
      if (!formData.department.trim()) {
        errors.department = text[language].departmentRequired;
      }

      // Update errors state
      setFormErrors(errors);
      // Return true if no errors
      return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault();

      // Only proceed if form is valid
      if (!validateForm()) {
        return;
      }

      // Call update function
      await handleUpdateEmployee(formData);
    };

    // Return modal JSX
    return (
      <div
        style={styles.modal}
        onClick={() => setShowAddEmployee(false)}
      >
        <div
          style={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].editEmployee}</h2>
            <button
              style={styles.closeBtn}
              onClick={() => {
                setShowEditEmployee(false);
                setSelectedEmployee(null);
              }}
            // onMouseEnter={handleCloseBtnMouseEnter}
            // onMouseLeave={handleCloseBtnMouseLeave}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].basicInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].fullName} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formInput,
                    borderColor: formErrors.name
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                  placeholder={text[language].enterFullName}
                />
                {formErrors.name && (
                  <span style={styles.errorText}>{formErrors.name}</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].emailAddress} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formInput,
                    borderColor: formErrors.email
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                  placeholder={text[language].enterEmail}
                />
                {formErrors.email && (
                  <span style={styles.errorText}>{formErrors.email}</span>
                )}
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].phoneNumber}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterPhone}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationalId}
                </label>
                <input
                  type="text"
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterNationalId}
                />
              </div>
            </div>

            {/* Work Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].workInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].department} *
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formSelect,
                    borderColor: formErrors.department
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <option value="">{text[language].selectDepartment}</option>
                  <option value="Information Technology">
                    {text[language].infotech}
                  </option>

                </select>
                {formErrors.department && (
                  <span style={styles.errorText}>{formErrors.department}</span>
                )}
              </div>

              {/* <div style={styles.formGroup}>
                <label style={styles.formLabel}>Labor Mode</label>
                <select
                  name="laborMode"
                  value={formData.laborMode}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">Select Labor Mode</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div> */}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].companyName}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterCompany}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].statusLabel}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="Active">{text[language].statusActive}</option>
                  <option value="OnLeave">
                    {text[language].statusOnLeave}
                  </option>
                  <option value="Inactive">
                    {text[language].statusInactive}
                  </option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].salaryLabel}
                </label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterSalary}
                  min="0"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].gender}</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">{text[language].selectGender}</option>
                  <option value="Male">{text[language].male}</option>
                  <option value="Female">{text[language].female}</option>
                </select>
              </div>
            </div>

            {/* Personal Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].personalInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].dateOfBirth}
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].maritalStatus}
                </label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">{text[language].selectMaritalStatus}</option>
                  <option value="Single">{text[language].single}</option>
                  <option value="Married">{text[language].married}</option>
                  <option value="Divorced">{text[language].divorced}</option>
                  <option value="Widowed">{text[language].widowed}</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationality}
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterNationality}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].city}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterCity}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].address}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterAddress}
              />
            </div>

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => {
                  setShowEditEmployee(false);
                  setSelectedEmployee(null);
                }}
              // onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#6b7280")}
              // onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                {text[language].cancel}
              </button>
              <button
                type="submit"
                style={{
                  ...styles.primaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                disabled={loading}
              // onMouseEnter={handlePrimaryBtnMouseEnter}
              // onMouseLeave={handlePrimaryBtnMouseLeave}
              >
                <Save size={screenSize === "xs" ? 14 : 16} />
                {loading ? text[language].updating : text[language].update}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE ADD EMPLOYEE MODAL COMPONENT =====
  const AddEmployeeModal = () => {
    // Initialize form data with empty values
    const [formData, setFormData] = useState({
      name: "",
      email: "",
      phone: "",
      department: "",
      position: "",
      laborMode: "",
      company: "",
      clientCode: "",
      status: "Active",
      salary: "",
      nationalId: "",
      address: "",
      city: "",
      country: "Egypt",
      nationality: "",
      gender: "",
      maritalStatus: "",
      dateOfBirth: "",
    });

    // Form validation errors state
    const [formErrors, setFormErrors] = useState({});

    // Handle input changes
    const handleInputChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });

      // Clear error when user starts typing
      if (formErrors[e.target.name]) {
        setFormErrors({
          ...formErrors,
          [e.target.name]: "",
        });
      }
    };

    // Validate form data
    const validateForm = () => {
      const errors = {};

      // Check required fields
      if (!formData.name.trim()) {
        errors.name = text[language].nameRequired;
      }

      if (!formData.email.trim()) {
        errors.email = text[language].emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = text[language].emailInvalid;
      }

      if (!formData.department.trim()) {
        errors.department = text[language].departmentRequired;
      }

      if (!formData.position.trim()) {
        errors.position = text[language].positionRequired;
      }

      // Update errors state
      setFormErrors(errors);
      // Return true if no errors
      return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault();

      // Only proceed if form is valid
      if (!validateForm()) {
        return;
      }

      // Call add employee function
      await handleAddEmployee(formData);
    };

    // Return modal JSX
    return (
      <div
        style={styles.modal}
        onClick={() => setShowAddEmployee(false)}
      >
        <div
          style={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].addNewEmployee}</h2>
            <button
              style={styles.closeBtn}
              onClick={() => setShowAddEmployee(false)}
            // onMouseEnter={handleCloseBtnMouseEnter}
            // onMouseLeave={handleCloseBtnMouseLeave}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].basicInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].fullName} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formInput,
                    borderColor: formErrors.name
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                  placeholder={text[language].enterFullName}
                />
                {formErrors.name && (
                  <span style={styles.errorText}>{formErrors.name}</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].emailAddress} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formInput,
                    borderColor: formErrors.email
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                  placeholder={text[language].enterEmail}
                />
                {formErrors.email && (
                  <span style={styles.errorText}>{formErrors.email}</span>
                )}
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].phoneNumber}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterPhone}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationalId}
                </label>
                <input
                  type="text"
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterNationalId}
                />
              </div>
            </div>

            {/* Work Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].workInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].department} *
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formSelect,
                    borderColor: formErrors.department
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <option value="">{text[language].selectDepartment}</option>
                  <option value="Information Technology">
                    {text[language].infotech}
                  </option>

                  <option value="Human Resources">
                    {text[language].humanResources}
                  </option>
                  <option value="Finance">{text[language].finance}</option>
                  <option value="Marketing">{text[language].marketing}</option>
                  <option value="Operations">
                    {text[language].operations}
                  </option>
                  <option value="Sales">{text[language].sales}</option>
                  <option value="Customer Service">
                    {text[language].customerService}
                  </option>
                </select>
                {formErrors.department && (
                  <span style={styles.errorText}>{formErrors.department}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].position} *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  style={{
                    ...styles.formInput,
                    borderColor: formErrors.position
                      ? "#ef4444"
                      : theme === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                  placeholder={text[language].enterPosition}
                />
                {formErrors.position && (
                  <span style={styles.errorText}>{formErrors.position}</span>
                )}
              </div>
            </div>

            {/* <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Labor Mode</label>
                <select
                  name="laborMode"
                  value={formData.laborMode}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">Select Labor Mode</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder="Enter company name"
                />
              </div>
            </div> */}

            {/* Show client code field only if company is not "infinity" */}
            {formData.company &&
              formData.company.toLowerCase() !== "infinity" && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    {text[language].clientCode}
                  </label>
                  <input
                    type="number"
                    name="clientCode"
                    value={formData.clientCode}
                    onChange={handleInputChange}
                    style={styles.formInput}
                    placeholder={text[language].enterClientCode}
                  />
                </div>
              )}

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].statusLabel}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="Active">{text[language].statusActive}</option>
                  <option value="OnLeave">
                    {text[language].statusOnLeave}
                  </option>
                  <option value="Inactive">
                    {text[language].statusInactive}
                  </option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].salaryLabel}
                </label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterSalary}
                  min="0"
                />
              </div>
            </div>

            {/* Personal Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].personalInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].dateOfBirth}
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].gender}</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="Select Gender">
                    {text[language].selectGender}
                  </option>
                  <option value="Male">{text[language].male}</option>
                  <option value="Female">{text[language].female}</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].maritalStatus}
                </label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">{text[language].selectMaritalStatus}</option>
                  <option value="Single">{text[language].single}</option>
                  <option value="Married">{text[language].married}</option>
                  <option value="Divorced">{text[language].divorced}</option>
                  <option value="Widowed">{text[language].widowed}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationality}
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterNationality}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].city}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterCity}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].country}</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterCountry}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].address}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterAddress}
              />
            </div>

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => setShowAddEmployee(false)}
              // onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#6b7280")}
              // onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                {text[language].cancel}
              </button>
              {/* <button
                type="submit"
                style={{
                  ...styles.primaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                disabled={loading}
              // onMouseEnter={handlePrimaryBtnMouseEnter}
              // onMouseLeave={handlePrimaryBtnMouseLeave}
              >
                <UserPlus size={screenSize === "xs" ? 14 : 16} />
                {loading ? text[language].adding : text[language].addEmployee}
              </button> */}
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Get hover styles for stat cards
  const getHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-2px)" : "translateY(-5px)",
    boxShadow:
      theme === "dark"
        ? screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0,0,0,0.4), inset 0 0.0625rem 0 rgba(255,255,255,0.2)"
          : "0 0.75rem 2.5rem rgba(0,0,0,0.4), inset 0 0.0625rem 0 rgba(255,255,255,0.2)"
        : screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0,0,0,0.15)"
          : "0 0.75rem 2.5rem rgba(0,0,0,0.15)",
    background:
      theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.95)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.2)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter:
      theme === "dark" ? "blur(1.875rem) saturate(200%)" : "blur(0.9375rem)",
    WebkitBackdropFilter:
      theme === "dark" ? "blur(1.875rem) saturate(200%)" : "blur(0.9375rem)",
  });

  // Default styles for cards when not hovered
  const getDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow:
      theme === "dark"
        ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
        : "0 0.5rem 2rem rgba(0,0,0,0.1)",
    background:
      theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
    WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
  });

  const handleMouseEnter = (e) => {
    const hoverStyles = getHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleMouseLeave = (e) => {
    const defaultStyles = getDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  // ===== RESPONSIVE ARCHIVE MODAL COMPONENT =====
  const ArchiveModal = () => (
    <div
      style={styles.modal}
      onClick={() => setShowArchive(false)}
    >
      <div
        style={styles.modalContent1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          {/* <h2 style={styles.modalTitle}>
            <Archive
              size={screenSize === "xs" ? 20 : 24}
              style={{ marginRight: spacing.gap }}
            />
            {text[language].viewArchive} ({archivedCount})
          </h2> */}
          <button
            style={styles.closeBtn}
            onClick={() => setShowArchive(false)}
          // onMouseEnter={handleCloseBtnMouseEnter}
          // onMouseLeave={handleCloseBtnMouseLeave}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Archive Description */}
        <div
          style={{
            background:
              theme === "dark"
                ? "rgba(107, 114, 128, 0.1)"
                : "rgba(107, 114, 128, 0.05)",
            border: `1px solid ${theme === "dark"
              ? "rgba(107, 114, 128, 0.3)"
              : "rgba(107, 114, 128, 0.2)"
              }`,
            borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
            padding: spacing.padding,
            marginBottom: spacing.margin,
            display: "flex",
            alignItems: "center",
            gap: spacing.gap,
            flexDirection: screenSize === "xs" ? "column" : "row",
            textAlign: screenSize === "xs" ? "center" : "left",
          }}
        >
          <Archive size={screenSize === "xs" ? 18 : 20} color="#6b7280" />
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.8)"
                  : "rgba(26, 26, 26, 0.7)",
              fontSize: fonts.body,
              lineHeight: "1.5",
            }}
          >
            {text[language].archivedDescription}
          </div>
        </div>

        {/* Archive Statistics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: screenSize === "xs" ? "1fr" : "repeat(2, 1fr)",
            gap: spacing.gap,
            marginBottom: spacing.margin,
          }}
        >
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(107, 114, 128, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {archivedCount}
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].totalArchived}
            </div>
          </div>
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#22c55e",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {
                archivedEmployees.filter(
                  (emp) =>
                    emp.archivedDate &&
                    new Date(emp.archivedDate) >
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                ).length
              }
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].archivedThisMonth}
            </div>
          </div>
        </div>

        {/* Archived Employees Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>{text[language].employee}</th>
                {screenSize !== "xs" && (
                  <th style={styles.tableHeader}>
                    {text[language].department}
                  </th>
                )}
                {screenSize !== "xs" && screenSize !== "sm" && (
                  <th style={styles.tableHeader}>{text[language].company}</th>
                )}
                <th style={styles.tableHeader}>
                  {text[language].archivedDate}
                </th>
                <th style={styles.tableHeader}>{text[language].actions}</th>
              </tr>
            </thead>
            <tbody>
              {archivedEmployees.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      screenSize === "xs"
                        ? "3"
                        : screenSize === "sm"
                          ? "4"
                          : "5"
                    }
                    style={{
                      ...styles.tableCell,
                      textAlign: "center",
                      padding: screenSize === "xs" ? "2rem" : "2.5rem",
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(26, 26, 26, 0.5)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: spacing.gap,
                      }}
                    >
                      <Archive
                        size={screenSize === "xs" ? 32 : 48}
                        color={
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(26, 26, 26, 0.3)"
                        }
                      />
                      <div>{text[language].noArchivedEmployees}</div>
                      <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                        {text[language].employeesArchivedMessage}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                archivedEmployees.map((emp) => (
                  <tr
                    key={emp.id}
                    style={{
                      ...styles.tableRow,
                      background:
                        theme === "dark"
                          ? "rgba(107, 114, 128, 0.05)"
                          : "rgba(107, 114, 128, 0.02)",
                    }}
                  >
                    {/* Employee Info */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.gap,
                        }}
                      >
                        {/* Profile Picture */}
                        {employeeProfilePics[emp.id] ? (
                          <img
                            src={employeeProfilePics[emp.id]}
                            alt={emp?.name || "Employee"}
                            style={{
                              ...styles.userAvatar,
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        <div
                          style={{
                            ...styles.userAvatar,
                            display: employeeProfilePics[emp.id] ? 'none' : 'flex'
                          }}
                        >
                          {getUserInitials(emp?.name)}
                        </div>

                        {/* Employee Name and Email */}
                        <div>
                          <div style={{ fontWeight: "600", fontSize: fonts.body }}>
                            {emp?.name?.split(" ").slice(0, 2).join(" ") || "N/A"}
                          </div>
                          <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                            {screenSize === "xs"
                              ? (emp?.email || "N/A").substring(0, 20) + "..."
                              : (emp?.email || "N/A")}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Department - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={styles.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <Building size={14} style={{ opacity: "0.6" }} />
                          {emp.department}
                        </div>
                      </td>
                    )}

                    {/* Company - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={styles.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <Building size={14} style={{ opacity: "0.6" }} />
                          {emp.company}
                        </div>
                      </td>
                    )}

                    {/* Archived Date */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Calendar size={14} style={{ opacity: "0.6" }} />
                        <span style={{ fontSize: fonts.small }}>
                          {emp.archivedDate
                            ? new Date(emp.archivedDate).toLocaleDateString(
                              "en-US",
                              {
                                year:
                                  screenSize === "xs" ? "2-digit" : "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )
                            : "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Restore Button */}
                        <button
                          style={{
                            ...styles.actionBtn,
                            ...styles.restoreBtn,
                          }}
                          title={text[language].restoreEmployee}
                          onClick={() => handleRestoreEmployee(emp.id)}
                        // onMouseEnter={(e) =>
                        //   handleActionBtnMouseEnter(e, "restore")
                        // }
                        // onMouseLeave={(e) =>
                        //   handleActionBtnMouseLeave(e, "restore")
                        // }
                        >
                          <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                        </button>

                        {/* View Button */}
                        <button
                          style={{
                            ...styles.actionBtn,
                            ...styles.viewBtn,
                          }}
                          title={text[language].viewDetails}
                          onClick={() => handleViewEmployee(emp)}
                        // onMouseEnter={(e) =>
                        //   handleActionBtnMouseEnter(e, "view")
                        // }
                        // onMouseLeave={(e) =>
                        //   handleActionBtnMouseLeave(e, "view")
                        // }
                        >
                          <Eye size={screenSize === "xs" ? 12 : 14} />
                        </button>

                        {/* Permanent Delete Button */}
                        {/* <button
                          style={{
                            ...styles.actionBtn,
                            ...styles.deleteBtn,
                          }}
                          title={text[language].permanentlyDelete}
                          onClick={() => {
                            if (
                              window.confirm(
                                text[language].confirmPermanentDelete
                              )
                            ) {
                              // Remove from archived employees
                              setArchivedEmployees(
                                archivedEmployees.filter((e) => e.id !== emp.id)
                              );
                              setArchivedCount(archivedCount - 1);
                              setSuccess(text[language].employeeDeleted);

                              // Optional: Make API call to permanently delete
                              apiFetch(`${API_URL}/employees/archived/${emp.id}`, { method: "DELETE" });
                            }
                          }}
                        // onMouseEnter={(e) =>
                        //   handleActionBtnMouseEnter(e, "delete")
                        // }
                        // onMouseLeave={(e) =>
                        //   handleActionBtnMouseLeave(e, "delete")
                        // }
                        >
                          <Trash2 size={screenSize === "xs" ? 12 : 14} />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Archive Actions */}
        {archivedEmployees.length > 0 && (
          <div
            style={{
              marginTop: spacing.margin,
              padding: spacing.padding,
              background:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border:
                theme === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.05)",
              flexDirection: screenSize === "xs" ? "column" : "row",
              gap: screenSize === "xs" ? spacing.gap : "0",
            }}
          >
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.body,
                textAlign: screenSize === "xs" ? "center" : "left",
              }}
            >
              {archivedEmployees.length}
              {archivedEmployees.length === 1
                ? text[language].archivedEmployee
                : text[language].archivedEmployees}
            </div>
            <div
              style={{
                display: "flex",
                gap: spacing.gap,
                flexWrap: "wrap",
                justifyContent: screenSize === "xs" ? "center" : "flex-end",
              }}
            >
              <button
                style={{
                  ...styles.secondaryBtn,
                  fontSize: fonts.small,
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                }}
                onClick={() => {
                  if (
                    window.confirm(
                      `${text[language].confirmRestoreAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion}`
                    )
                  ) {
                    // Restore all archived employees
                    archivedEmployees.forEach((emp) =>
                      handleRestoreEmployee(emp.id)
                    );
                  }
                }}
              // onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#22c55e")}
              // onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                {screenSize !== "xs" && text[language].restoreAll}
              </button>
              <button
                style={{
                  ...styles.secondaryBtn,
                  fontSize: fonts.small,
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                  color: "#ef4444",
                  borderColor: "rgba(239, 68, 68, 0.3)",
                }}
                onClick={() => {
                  if (
                    window.confirm(
                      `${text[language].confirmDeleteAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion} ${text[language].cannotBeUndone}`
                    )
                  ) {
                    // Clear all archived employees
                    setArchivedEmployees([]);
                    setArchivedCount(0);
                    setSuccess(text[language].allEmployeesDeleted);

                    // Optional: Make API call to clear archived employees
                    apiFetch(`${API_URL}/employeesa/archived`, { method: "DELETE" });
                  }
                }}
              // onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#ef4444")}
              // onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <Trash2 size={screenSize === "xs" ? 12 : 14} />
                {screenSize !== "xs" && text[language].deleteAll}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ===== RESPONSIVE ALERT COMPONENT =====
  const Alert = ({ type, message, onClose }) => {
    // Auto close after 5 seconds
    useEffect(() => {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      // Cleanup timer on component unmount
      return () => clearTimeout(timer);
    }, [onClose]);

    // Return alert JSX
    return (
      <div
        style={{
          ...styles.alert,
          backgroundColor:
            type === "error"
              ? "rgba(239, 68, 68, 0.1)"
              : "rgba(34, 197, 94, 0.1)",
          borderColor: type === "error" ? "#ef4444" : "#22c55e",
          color: type === "error" ? "#ef4444" : "#22c55e",
        }}
      >
        {type === "error" ? (
          <AlertCircle size={screenSize === "xs" ? 14 : 16} />
        ) : (
          <CheckCircle size={screenSize === "xs" ? 14 : 16} />
        )}
        <span style={{ fontSize: fonts.body }}>{message}</span>
        <button onClick={onClose} style={styles.alertClose}>
          <X size={screenSize === "xs" ? 12 : 14} />
        </button>
      </div>
    );
  };

  // ===== MAIN COMPONENT RETURN =====
  try {
    return (
      <div style={styles.container}>
        {/* ===== RESPONSIVE GLOBAL STYLES ===== */}
        <style>
          {`
          /* ===== RESPONSIVE CSS ANIMATIONS ===== */
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(${screenSize === "xs" ? "0.5rem" : "1rem"});
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 1; 
          }
          50% { 
            transform: scale(${screenSize === "xs" ? "1.05" : "1.15"}); 
            opacity: 0.8; 
          }
        }
          select option {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#1715247e"} !important;
    color: #fff !important;
    padding: 12px;
    
  }
  
  select option:hover {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#3a39457e"} !important;
  }

        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(${screenSize === "xs" ? "0.5rem" : "0.625rem"
            }); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        /* ===== RESPONSIVE SCROLLBAR STYLES ===== */
        ::-webkit-scrollbar {
          width: ${screenSize === "xs"
              ? "0.25rem"
              : screenSize === "sm"
                ? "0.375rem"
                : "0.5rem"
            };
          height: ${screenSize === "xs"
              ? "0.25rem"
              : screenSize === "sm"
                ? "0.375rem"
                : "0.5rem"
            };
        }

        ::-webkit-scrollbar-track {
          background: ${theme === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)"
            };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
            };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
            };
        }
          .modal::-webkit-scrollbar {
  display: none;
}


        /* ===== RESPONSIVE BODY STYLES ===== */
        body {
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
        }

        /* ===== RESPONSIVE UTILITY CLASSES ===== */
        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .pulse-animation {
          animation: pulse 2s infinite;
        }

        /* ===== RESPONSIVE FOCUS STYLES ===== */
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 ${screenSize === "xs" ? "0.125rem" : "0.25rem"
            } rgba(59, 130, 246, 0.15) !important;
        }

        /* ===== RESPONSIVE TABLE STYLES ===== */
        table {
          border-collapse: separate;
          border-spacing: 0;
        }

        /* ===== RESPONSIVE BUTTON STYLES ===== */
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ===== RESPONSIVE MEDIA QUERIES ===== */
        @media (max-width: 480px) {
          .hide-on-mobile {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .hide-on-tablet {
            display: none !important;
          }
        }

        @media (max-width: 1024px) {
          .hide-on-desktop {
            display: none !important;
          }
        }
        `}
        </style>

        {/* Error Alert */}
        {error && (
          <Alert type="error" message={error} onClose={() => setError("")} />
        )}

        {/* Success Alert */}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess("")}
          />
        )}

        {/* Welcome Section */}
        <div
          style={styles.statCard}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
        >
          <div style={styles.welcomeSection}>
            <h1 style={styles.welcomeTitle}>{text[language].title}</h1>
            {/* <p style={styles.welcomeSubtitle}>
            Comprehensive management of employee data and status tracking
          </p> */}
          </div>
        </div>

        {/* Action Buttons Section */}
        <div style={styles.statCard}>
          <div style={styles.topButtonsContainer}>
            {/* Add Employee Button */}
            {/* <button
            style={{
              ...styles.primaryBtn,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={() => setShowAddEmployee(true)}
            disabled={loading}
          // onMouseEnter={handlePrimaryBtnMouseEnter}
          // onMouseLeave={handlePrimaryBtnMouseLeave}
          >
            <Plus size={screenSize === "xs" ? 14 : 16} />
            {screenSize !== "xs" && text[language].addEmployee}
            {screenSize === "xs" && text[language].add}
          </button> */}

            {/* Export/Share Button */}
            <ShareButton
              handleExportToExcel={handleExportToExcel}
              handleShareReport={handleShareReport}
              loading={loading}
              styles={styles}
              handleSecondaryBtnMouseEnter={handleSecondaryBtnMouseEnter}
              handleSecondaryBtnMouseLeave={handleSecondaryBtnMouseLeave}
            />
          </div>
        </div>

        {/* Employee List Section */}
        <div
          style={styles.statCard}
        // onMouseEnter={handleCardMouseEnter}
        // onMouseLeave={handleCardMouseLeave}
        >
          <h3 style={styles.welcomeTitle}>
            <Users size={screenSize === "xs" ? 18 : 20} />
            {screenSize !== "xs" &&
              `${text[language].employeeList} (${filteredEmployees.length} ${text[language].employeesCount})`}
            {screenSize === "xs" &&
              `${text[language].employees} (${filteredEmployees.length})`}
            {loading && (
              <span style={{ fontSize: fonts.body, opacity: 0.7 }}>
                {" "}
                - {text[language].loading}
              </span>
            )}
          </h3>

          {/* Filter Controls */}
          <div style={styles.filterContainer}>
            {/* Search Input */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>
                {text[language].searchEmployee}
              </label>
              <input
                type="text"
                placeholder={text[language].searchNameIdEmail}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.filterInput}
              />
            </div>

            {/* Department Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>
                {text[language].department}
              </label>

              <input
                type="text"
                placeholder={text[language].searchDepartment}
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                style={styles.filterInput}
              />
            </div>

            {/* Status Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>{text[language].Status}</label>
              <div style={{ position: 'relative' }}>
                <ReusableDropdown
                  items={[
                    { key: '', label: text[language].allStatuses },
                    { key: 'Active', label: text[language].statusActive },
                    { key: 'OnLeave', label: text[language].statusOnLeave },
                    { key: 'Inactive', label: text[language].statusInactive },
                  ]}
                  value={filterStatusSearch}
                  onChange={(val) => setFilterStatusSearch(val)}
                  onSelect={(item) => {
                    setFilterStatus(item.key);
                    setFilterStatusSearch(item.label);
                  }}
                  placeholder={text[language].selectStatus || text[language].Status}
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  maxItems={6}
                />
              </div>
            </div>
            {screenSize !== "xs" && (
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>{text[language].company}</label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={[{ key: '', label: text[language].allCompanies }, ...(companies || []).map(c => ({ key: c, label: c }))]}
                    value={filterCompanySearch}
                    onChange={(val) => setFilterCompanySearch(val)}
                    onSelect={(item) => {
                      setFilterCompany(item.key || '');
                      setFilterCompanySearch(item.label || '');
                    }}
                    placeholder={text[language].allCompanies}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                    maxItems={8}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Responsive Table with Headers */}
          <div style={styles.tableContainer}>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <User size={14} />
                        {text[language].employee}
                      </div>
                    </th>
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <th style={styles.tableHeader}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <Building size={14} />
                          {text[language].department}
                        </div>
                      </th>
                    )}
                    {screenSize !== "xs" && (
                      <th style={styles.tableHeader}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <Calendar size={14} />
                          {text[language].joinDate}
                        </div>
                      </th>
                    )}
                    <th style={styles.tableHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Award size={14} />
                        {text[language].actions}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id} style={styles.tableRow}>
                      {/* Employee Info with hierarchy indentation and expand/collapse */}
                      <td style={styles.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.gap,
                            paddingLeft: `${emp.level * (screenSize === "xs" ? 1 : 2)}rem`,
                          }}
                        >
                          {/* Expand/Collapse button for employees with children */}
                          {(emp.level === 0 || emp.hasChildren) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleEmployeeExpand(emp.id);
                              }}
                              style={{
                                background:
                                  theme === "dark"
                                    ? "rgba(59, 130, 246, 0.1)"
                                    : "rgba(59, 130, 246, 0.08)",
                                border: `1px solid ${theme === "dark"
                                  ? "rgba(59, 130, 246, 0.3)"
                                  : "rgba(59, 130, 246, 0.2)"
                                  }`,
                                borderRadius: "0.375rem",
                                cursor: "pointer",
                                padding: "0.375rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#2e7cba",
                                transition: "all 0.2s ease",
                                width: "1.75rem",
                                height: "1.75rem",
                              }}
                              title={emp.isExpanded ? "Collapse" : "Expand"}
                            // onMouseEnter={(e) => {
                            //   e.currentTarget.style.background =
                            //     theme === "dark"
                            //       ? "rgba(59, 130, 246, 0.2)"
                            //       : "rgba(59, 130, 246, 0.15)";
                            //   e.currentTarget.style.transform = "scale(1.1)";
                            // }}
                            // onMouseLeave={(e) => {
                            //   e.currentTarget.style.background =
                            //     theme === "dark"
                            //       ? "rgba(59, 130, 246, 0.1)"
                            //       : "rgba(59, 130, 246, 0.08)";
                            //   e.currentTarget.style.transform = "scale(1)";
                            // }}
                            >
                              {emp.isExpanded ? (
                                <ChevronDown size={14} strokeWidth={2.5} />
                              ) : (
                                <ChevronUp size={14} strokeWidth={2.5} style={{ transform: "rotate(-90deg)" }} />
                              )}
                            </button>
                          )}

                          {/* Show manager badge if has children */}
                          {emp.hasChildren && (
                            <div
                              style={{
                                background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
                                borderRadius: "0.375rem",
                                padding: "0.25rem 0.5rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                              }}
                            >
                              <Users size={12} color="white" />
                              <span
                                style={{
                                  fontSize: fonts.tiny,
                                  color: "white",
                                  fontWeight: "600",
                                }}
                              >
                                Manager
                              </span>
                            </div>
                          )}

                          {/* Profile Picture (show image if available, otherwise initials) */}
                          {employeeProfilePics[emp.id] ? (
                            <img
                              src={employeeProfilePics[emp.id]}
                              alt={emp?.name || "Employee"}
                              style={{
                                ...styles.userAvatar,
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}

                          <div
                            style={{
                              ...styles.userAvatar,
                              display: employeeProfilePics[emp.id] ? "none" : "flex",
                            }}
                          >
                            {getUserInitials(emp?.name)}
                          </div>
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: fonts.body,
                                color: emp.hasChildren ? "#2e7cba" : "inherit",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              {/* Status Circle Indicator */}
                              <div
                                style={{
                                  width: screenSize === "xs" ? "0.625rem" : "0.75rem",
                                  height: screenSize === "xs" ? "0.625rem" : "0.75rem",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    emp.status === "Active"
                                      ? "#10b981"
                                      : emp.status === "OnLeave"
                                        ? "#f59e0b"
                                        : "#ef4444",
                                  boxShadow: `0 0 0 2px ${theme === "dark"
                                    ? "rgba(255, 255, 255, 0.1)"
                                    : "rgba(0, 0, 0, 0.05)"
                                    }, 0 0 8px ${emp.status === "Active"
                                      ? "rgba(16, 185, 129, 0.4)"
                                      : emp.status === "OnLeave"
                                        ? "rgba(245, 158, 11, 0.4)"
                                        : "rgba(239, 68, 68, 0.4)"
                                    }`,
                                  animation: "pulse 2s infinite",
                                  flexShrink: 0,
                                }}
                                title={
                                  emp.status === "Active"
                                    ? text[language].statusActive
                                    : emp.status === "OnLeave"
                                      ? text[language].statusOnLeave
                                      : text[language].statusInactive
                                }
                              />
                              {emp.name.split(" ").slice(0, 2).join(" ")}
                            </div>
                            {emp.department && (
                              <div
                                style={{
                                  fontSize: fonts.tiny,
                                  opacity: "0.6",
                                  marginTop: "0.125rem",
                                  color: "#2e7cba",
                                }}
                              >
                                {emp.department}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Department */}
                      {screenSize !== "xs" && screenSize !== "sm" && (
                        <td style={styles.tableCell}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <Building size={14} style={{ opacity: "0.6" }} />
                            {emp.department}
                          </div>
                        </td>
                      )}

                      {/* Join Date */}
                      {screenSize !== "xs" && (
                        <td style={styles.tableCell}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <Calendar size={14} style={{ opacity: "0.6" }} />
                            {emp.joinDate
                              ? new Date(emp.joinDate).toLocaleDateString("en-US", {
                                year: screenSize === "sm" ? "2-digit" : "numeric",
                                month: "short",
                                day: "numeric",
                              })
                              : "N/A"}
                          </div>
                        </td>
                      )}

                      {/* Actions */}
                      <td style={styles.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            style={{ ...styles.actionBtn, ...styles.viewBtn }}
                            title={text[language].viewDetails}
                            onClick={() => handleViewEmployee(emp)}
                          // onMouseEnter={(e) => handleActionBtnMouseEnter(e, "view")}
                          // onMouseLeave={(e) => handleActionBtnMouseLeave(e, "view")}
                          >
                            <Eye size={screenSize === "xs" ? 12 : 14} />
                          </button>

                          <button
                            style={{
                              ...styles.actionBtn,
                              background: "rgba(139, 92, 246, 0.1)",
                              color: "#8b5cf6",
                              border: "1px solid rgba(139, 92, 246, 0.2)",
                            }}
                            title={text[language].viewDocuments}
                            onClick={() => handleViewDocuments(emp)}
                          >
                            <FileText size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Responsive Statistics Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gap: spacing.gap,
            marginTop: spacing.margin,
          }}
        >
          {/* Total Employees Card */}
          <div
            style={styles.statCard}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
                flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
              }}
            >
              <Users size={screenSize === "xs" ? 14 : 16} />
              {screenSize === "xs"
                ? text[language].total
                : text[language].totalEmployees}
            </h4>
            <p
              style={{
                color: "#10b981",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {employeeCount}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {screenSize === "xs"
                ? text[language].allEmployees
                : text[language].allRegisteredEmployees}
            </p>
          </div>

          {/* Active Employees Card */}
          <div
            style={styles.statCard}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
                flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
              }}
            >
              <UserCheck size={screenSize === "xs" ? 14 : 16} />
              {screenSize === "xs"
                ? text[language].active
                : text[language].activeEmployees}
            </h4>
            <p
              style={{
                color: "#10b981",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {counts.Active}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {text[language].currentlyWorking}
            </p>
          </div>

          {/* On Leave Card */}
          <div
            style={styles.statCard}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
                flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
              }}
            >
              <Calendar size={screenSize === "xs" ? 14 : 16} />
              {text[language].onLeave}
            </h4>
            <p
              style={{
                color: "#f59e0b",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {counts.OnLeave}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {text[language].currentlyOnLeave}
            </p>
          </div>

          {/* Archived Card */}
          <div
            style={styles.statCard}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
                flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
              }}
            >
              <Archive size={screenSize === "xs" ? 14 : 16} />
              {text[language].archived}
            </h4>
            <p
              style={{
                color: "#f59e0b",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {archivedCount}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {text[language].archivedEmployees}
            </p>
          </div>

          {/* Recent Employees Card */}
          <div
            style={styles.statCard}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
                flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
              }}
            >
              <Calendar size={screenSize === "xs" ? 14 : 16} />
              {text[language].newEmployees}
            </h4>
            <p
              style={{
                color: "#f59e0b",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {counts.Recent}
            </p>
            <p
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                margin: "0.25rem 0 0 0",
              }}
            >
              {text[language].joinedRecently}
            </p>
          </div>
        </div>

        {/* Render Modals */}
        {showAddEmployee && <AddEmployeeModal />}
        {showViewEmployee && <ViewEmployeeModal />}
        {showEditEmployee && <EditEmployeeModal />}
        {showArchive && <ArchiveModal />}
        {showDocumentsModal && <DocumentsModal />}
      </div>
    );
  } catch (error) {
    console.error('Error rendering EmployeeManagementManager:', error);
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#ef4444'
      }}>
        <h1>Error Loading Employee Management</h1>
        <p>{error.message}</p>
        <p>Please check the console for more details.</p>
      </div>
    );
  }
}

// Export the component using export default syntax
export default EmployeeManagementManager;
